import { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { useSearchParams, useParams } from "react-router-dom";
import useViewPreference from '../hooks/useViewPreference';
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import ViewSelector from "../components/ViewSelector";
import KanbanView from "../components/KanbanView";
import CardsView from "../components/CardsView";
import ListView from "../components/ListView";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import {
  Package,
  Loader2,
  Search,
  Plus,
  Edit2,
  Trash2,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Send,
  MapPin,
  FileText,
  User,
  Filter,
  Upload,
  Download,
  Eye,
  FormInput,
  Navigation,
  Calendar,
  PackageCheck,
  X,
  LayoutGrid,
  List,
  Table2,
  Columns3,
  Phone,
  Smartphone,
  ChevronsUpDown,
  Check,
  UserPlus,
  FileSpreadsheet,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Users,
  Building2,
  RotateCcw,
  FileBarChart,
  Mail,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Stethoscope,
} from "lucide-react";
import { ClienteAtencionWatcher } from "../contexts/AtencionPrioritariaContext";

// Utility function to calculate days between dates
const calcularDias = (fechaInicio, fechaFin = new Date()) => {
  if (!fechaInicio) return null;
  const inicio = new Date(fechaInicio);
  const fin = new Date(fechaFin);
  const diffTime = Math.abs(fin - inicio);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Component to display time indicators based on status
const TimeIndicators = ({ solicitud }) => {
  const { estado, fecha_creacion, fecha_asignacion, fecha_entrega, mensajero_id } = solicitud;
  
  const diasDesdeCreacion = calcularDias(fecha_creacion);
  const diasDesdeAsignacion = calcularDias(fecha_asignacion);
  const diasAsignacionEntrega = fecha_asignacion && fecha_entrega 
    ? calcularDias(fecha_asignacion, fecha_entrega) 
    : null;
  
  const getColorClass = (dias) => {
    if (dias === null) return 'text-slate-400';
    if (dias <= 1) return 'text-green-600 dark:text-green-400';
    if (dias <= 3) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };
  
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {/* Pendiente: Show days since creation */}
      {estado === 'pendiente' && (
        <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-50 dark:bg-yellow-900/20 ${getColorClass(diasDesdeCreacion)}`}>
          <Clock className="w-3 h-3" />
          <span>{diasDesdeCreacion}d pendiente</span>
        </div>
      )}
      
      {/* En Proceso: Show days since creation AND days since assignment if available */}
      {estado === 'en_proceso' && (
        <>
          <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 ${getColorClass(diasDesdeCreacion)}`}>
            <Clock className="w-3 h-3" />
            <span>{diasDesdeCreacion}d creado</span>
          </div>
          {mensajero_id && diasDesdeAsignacion !== null ? (
            <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 ${getColorClass(diasDesdeAsignacion)}`}>
              <User className="w-3 h-3" />
              <span>{diasDesdeAsignacion}d asignado</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400">
              <User className="w-3 h-3" />
              <span>Sin mensajero</span>
            </div>
          )}
        </>
      )}
      
      {/* Completado: Show all time metrics */}
      {estado === 'completado' && (
        <>
          <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 ${getColorClass(diasDesdeCreacion)}`}>
            <Clock className="w-3 h-3" />
            <span>{diasDesdeCreacion}d total</span>
          </div>
          {diasDesdeAsignacion !== null && (
            <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
              <User className="w-3 h-3" />
              <span>{diasDesdeAsignacion}d asignado</span>
            </div>
          )}
          {diasAsignacionEntrega !== null && (
            <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
              <CheckCircle2 className="w-3 h-3" />
              <span>{diasAsignacionEntrega}d entrega</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default function SolicitudesMensajeriaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { solicitudId: urlSolicitudId } = useParams();
  const [solicitudes, setSolicitudes] = useState([]);
  const [mensajeros, setMensajeros] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSolicitud, setEditingSolicitud] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Initialize estadoFilter from URL query param or default to "all"
  const [estadoFilter, setEstadoFilter] = useState(() => {
    const urlEstado = searchParams.get("estado");
    return urlEstado || "all";
  });
  const [urgenteFilter, setUrgenteFilter] = useState("all");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingPlantilla, setLoadingPlantilla] = useState(false);
  // Custom template fields state
  const [templateFields, setTemplateFields] = useState([]);
  const [customFieldsData, setCustomFieldsData] = useState({});
  // Delivery confirmation state
  const [showEntregaDialog, setShowEntregaDialog] = useState(false);
  const [selectedSolicitudForEntrega, setSelectedSolicitudForEntrega] = useState(null);
  const [entregaData, setEntregaData] = useState({
    fecha_entrega: "",
    soporte_file: null
  });
  const [uploadingSoporte, setUploadingSoporte] = useState(false);
  // App settings for photo required validation
  const [appSettings, setAppSettings] = useState({ photo_required: false });
  // View dialog state
  const [showViewDialog, setShowViewDialog] = useState(false);
  // View mode state
  const [viewMode, setViewMode] = useViewPreference("mensajeria_solicitudes", "table");
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const fileInputRef = useRef(null);
  const soporteInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  const { getAuthHeaders, user } = useAuth();

  // Bulk import states
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkStep, setBulkStep] = useState(1); // 1: upload, 2: mapping, 3: result
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkMapping, setBulkMapping] = useState({});
  const [bulkResult, setBulkResult] = useState(null);

  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);

  // Client-side pagination — keeps the list fast as the history grows.
  // Export & "Seleccionar todo" siguen operando sobre TODO el set filtrado.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Clientes state
  const [clientes, setClientes] = useState([]);
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  // Monthly messenger report — UI state
  const [showReporteMensualDialog, setShowReporteMensualDialog] = useState(false);
  const [showDiagnostico, setShowDiagnostico] = useState(false);
  const [reparando, setReparando] = useState(false);
  const [diagnosticoData, setDiagnosticoData] = useState(null);
  const [diagnosticoLoading, setDiagnosticoLoading] = useState(false);
  const [diagnosticoError, setDiagnosticoError] = useState(null);
  const [diagnosticoNonce, setDiagnosticoNonce] = useState(0);

  // Verificar Imagen (admin) — diálogo para probar un asset_id puntual
  const [showVerificarImagen, setShowVerificarImagen] = useState(false);
  const [verificarUrl, setVerificarUrl] = useState("");
  const [verificandoImagen, setVerificandoImagen] = useState(false);
  const [verificarResultado, setVerificarResultado] = useState(null);

  // Dispara el fetch del diagnóstico cada vez que el diálogo se abre o el usuario pulsa Reintentar/Actualizar.
  useEffect(() => {
    if (!showDiagnostico) return;
    let abort = false;
    setDiagnosticoLoading(true);
    setDiagnosticoError(null);
    setDiagnosticoData(null);
    const token = localStorage.getItem("token");
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    // Usamos axios (no fetch nativo) para evitar errores con extensiones del navegador
    // (password managers, ad-blockers) que interceptan la Fetch API y rompen postMessage.
    (async () => {
      try {
        const r = await axios.get(`${apiUrl}/api/entregas/diagnostico`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000,
        });
        if (!abort) {
          setDiagnosticoData(r.data);
          if (diagnosticoNonce > 0) {
            toast.success(`Diagnóstico actualizado: ${r.data.total_registros} registros, ${r.data.binario_perdido} sin binario.`);
          }
        }
      } catch (e) {
        if (!abort) {
          let msg = e?.message || String(e);
          // Si es respuesta del backend con detalle, usar ese
          if (e?.response) {
            const status = e.response.status;
            const detail = e.response.data?.detail || e.response.statusText || "";
            msg = `HTTP ${status}${detail ? " · " + detail : ""}`;
          } else if (e?.code === "ECONNABORTED") {
            msg = "Timeout: el servidor tardó demasiado en responder";
          }
          setDiagnosticoError(msg);
          toast.error(`No se pudo cargar el diagnóstico: ${msg}`);
        }
      } finally {
        if (!abort) setDiagnosticoLoading(false);
      }
    })();
    return () => { abort = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDiagnostico, diagnosticoNonce]);

  const handleRepararBinarios = async () => {
    if (!window.confirm("¿Iniciar reparación de imágenes perdidas?\n\nEsto escaneará todos los acuses sin imagen y recuperará los archivos que estén en disco. La tarea corre EN SEGUNDO PLANO — podés seguir trabajando mientras tanto.")) return;
    setReparando(true);
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    try {
      // 1) Disparar la tarea (responde inmediato con {ok, ya_corriendo, estado, mensaje})
      const r = await axios.post(`${apiUrl}/api/entregas/reparar-binarios`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000, // 30s — la respuesta es inmediata, no esperamos el escaneo
      });
      if (r.data?.ya_corriendo) {
        toast.info("Ya hay una reparación en curso. Te avisaremos cuando termine.", { duration: 5000 });
      } else {
        toast.success("✅ Reparación iniciada en segundo plano. Te avisaremos cuando termine.", { duration: 5000 });
      }

      // 2) Polling al endpoint /status hasta que running=false (o 10 min máx)
      const start = Date.now();
      const MAX_MS = 10 * 60 * 1000; // 10 minutos
      const tick = async () => {
        if (Date.now() - start > MAX_MS) {
          toast.warning("⏰ La reparación está tardando más de 10 minutos. Consultá los logs del servidor.", { duration: 8000 });
          setReparando(false);
          return;
        }
        try {
          const s = await axios.get(`${apiUrl}/api/entregas/reparar-binarios/status`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          });
          const st = s.data || {};
          if (st.running) {
            // Sigue corriendo — re-poll en 4s
            setTimeout(tick, 4000);
          } else {
            // Terminó
            const recovered = st.recovered_from_disk || 0;
            const orphan = st.orphan_disk_attached || 0;
            const noSource = st.no_source_found || 0;
            const markedBroken = st.marked_broken || 0;
            const errors = st.errors || 0;
            const scanned = st.scanned || 0;
            const totalRecovered = recovered + orphan;
            if (totalRecovered > 0) {
              toast.success(
                `✅ Reparación completada — ${totalRecovered} imágenes recuperadas` +
                (recovered > 0 ? ` (${recovered} del filesystem` : "") +
                (orphan > 0 ? `${recovered > 0 ? ", " : " ("}${orphan} archivos huérfanos re-attachados` : "") +
                (recovered > 0 || orphan > 0 ? ")" : "") +
                (markedBroken > 0 ? `. ${markedBroken} marcadas como irrecuperables.` : "") +
                (noSource > 0 ? ` ${noSource} históricos sin archivo.` : ""),
                { duration: 12000 }
              );
            } else if (markedBroken > 0) {
              toast.warning(
                `⚠️ ${markedBroken} acuses históricos marcados como irrecuperables (binario perdido antes del fix Feb 2026). El sistema mostrará placeholder limpio en lugar del ícono roto.`,
                { duration: 12000 }
              );
            } else if (scanned > 0 && noSource > 0) {
              toast.warning(
                `⚠️ Escaneados ${scanned} acuses sin imagen, pero ${noSource} no tienen archivo en disco.`,
                { duration: 10000 }
              );
            } else {
              toast.info("Todo en orden — no hay imágenes que reparar.", { duration: 6000 });
            }
            if (errors > 0) toast.error(`${errors} errores durante la reparación (ver logs)`);
            setReparando(false);
          }
        } catch (pollErr) {
          // No abortamos por un error puntual de polling — reintenta
          setTimeout(tick, 6000);
        }
      };
      // Primer poll a los 3s
      setTimeout(tick, 3000);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Error desconocido";
      toast.error(`Error al iniciar la reparación: ${detail}`);
      setReparando(false);
    }
  };

  // Extrae el asset_id de una URL completa o lo deja tal cual si ya es un UUID
  const extraerAssetId = (input) => {
    if (!input) return "";
    const trimmed = input.trim();
    const match = trimmed.match(/\/entregas\/imagen\/([a-f0-9\-]+)/i);
    return match ? match[1] : trimmed;
  };

  const handleVerificarImagen = async () => {
    const assetId = extraerAssetId(verificarUrl);
    if (!assetId) {
      toast.error("Pegá una URL o un asset_id válido.");
      return;
    }
    setVerificandoImagen(true);
    setVerificarResultado(null);
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const t0 = Date.now();
    try {
      // 1) Llamada al endpoint de imagen sin descargar el binario completo (HEAD-style con range)
      const r = await fetch(`${apiUrl}/api/entregas/imagen/${assetId}`, {
        method: "GET",
        headers: { Range: "bytes=0-0" }, // pedimos solo 1 byte para no descargar 5MB
      }).catch((err) => ({ _networkError: err.message }));

      const ms = Date.now() - t0;

      if (r._networkError) {
        setVerificarResultado({
          ok: false,
          status: "NETWORK_ERROR",
          ms,
          mensaje: `Error de red: ${r._networkError}`,
          recomendacion: "El backend no respondió. Reintentá en unos segundos.",
        });
        return;
      }

      const recoveredFrom = r.headers.get("X-Recovered-From");
      const contentType = r.headers.get("Content-Type") || "";
      let detail = null;
      if (!r.ok && contentType.includes("application/json")) {
        try { detail = (await r.json()).detail; } catch (_) { /* ignore */ }
      }

      // 2) También consultamos el diagnostico/asset/{id} para info técnica
      let diag = null;
      try {
        const dr = await axios.get(`${apiUrl}/api/entregas/diagnostico/asset/${assetId}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 20000,
        });
        diag = dr.data;
      } catch (_) { /* admin-only o falla — no es crítico */ }

      let veredicto;
      let mensaje;
      let recomendacion;
      let nivel; // "ok" | "warn" | "error"

      if (r.status === 200 || r.status === 206) {
        nivel = "ok";
        veredicto = "Imagen accesible ✅";
        mensaje = `La imagen se sirvió correctamente en ${ms}ms${recoveredFrom ? ` (recuperada desde: ${recoveredFrom})` : " (desde BD)"}`;
        recomendacion = "El endpoint funciona perfectamente. Si la veías rota en la UI, hacé Ctrl+F5 para limpiar caché del browser.";
      } else if (r.status === 410) {
        nivel = "warn";
        veredicto = "Asset existe pero su binario está perdido ⚠️";
        mensaje = detail || "El registro existe en BD pero el archivo (data) está vacío y no se encontró en disco.";
        recomendacion = "Este asset fue afectado por el bug histórico (anterior al fix de Feb 2026). El binario está perdido permanentemente. Las nuevas entregas ya no sufren este problema.";
      } else if (r.status === 404) {
        nivel = "warn";
        veredicto = "Asset no existe en la BD ⚠️";
        mensaje = "No hay registro con ese asset_id en delivery_assets.";
        recomendacion = "Verificá que copiaste la URL completa. Si era un asset legítimo, fue borrado de la BD.";
      } else if (r.status === 503) {
        nivel = "error";
        veredicto = "Timeout de base de datos ❌";
        mensaje = detail || "El meta-lookup excedió el tiempo permitido — falta índice en `id`.";
        recomendacion = "El índice `id_unique_idx` no está creado en producción. Hacé redeploy del backend (el startup lo crea automáticamente). Si persiste, contactá a Emergent Support.";
      } else if (r.status === 422) {
        nivel = "warn";
        veredicto = "Archivo corrupto ⚠️";
        mensaje = detail || "El binario está en BD pero no se pudo decodificar.";
        recomendacion = "El archivo está dañado. No se puede recuperar automáticamente.";
      } else {
        nivel = "error";
        veredicto = `HTTP ${r.status} ❌`;
        mensaje = detail || "Respuesta inesperada del servidor.";
        recomendacion = "Mandale este resultado al equipo de desarrollo.";
      }

      setVerificarResultado({
        ok: nivel === "ok",
        nivel,
        status: r.status,
        ms,
        recoveredFrom,
        veredicto,
        mensaje,
        recomendacion,
        diag,
        assetId,
      });
    } catch (e) {
      setVerificarResultado({
        ok: false,
        nivel: "error",
        status: "EXCEPTION",
        ms: Date.now() - t0,
        veredicto: "Error inesperado ❌",
        mensaje: e.message,
        recomendacion: "Recargá la página e intentá de nuevo.",
      });
    } finally {
      setVerificandoImagen(false);
    }
  };

  const [reporteMensualConfig, setReporteMensualConfig] = useState(null);
  const [reporteMensualForm, setReporteMensualForm] = useState({
    activo: false,
    dia_envio: 1,
    hora_envio: "09:00",
    emails_adicionales: [],
    incluir_admin_original: true,
  });
  const [reporteMensualSavingFlag, setReporteMensualSavingFlag] = useState(false);
  const [reporteMensualSendingFlag, setReporteMensualSendingFlag] = useState(false);
  const [reporteMensualEmailInput, setReporteMensualEmailInput] = useState("");
  const [reporteMensualHistorial, setReporteMensualHistorial] = useState([]);
  const [reporteMensualResendingId, setReporteMensualResendingId] = useState(null);
  const [clienteSearchQuery, setClienteSearchQuery] = useState("");
  const [showNewClienteDialog, setShowNewClienteDialog] = useState(false);
  const [newClienteData, setNewClienteData] = useState({
    nombre: "",
    direccion: "",
    telefono: "",
    celular: "",
    sucursal: "N/A"
  });

  // Sucursal options
  const SUCURSALES_OPTIONS = [
    { value: "Sucursal Constanza", label: "Sucursal Constanza" },
    { value: "Sucursal Terrenas", label: "Sucursal Terrenas" },
    { value: "Sucursal Bavaro", label: "Sucursal Bavaro" }
  ];

  // Campos disponibles para mapeo en importación Excel
  const solicitudFields = [
    { value: "cliente", label: "Cliente" },
    { value: "direccion_envio", label: "Dirección de Envío" },
    { value: "telefono_cliente", label: "Teléfono Cliente" },
    { value: "celular_cliente", label: "Celular Cliente" },
    { value: "tipo_documento_nombre", label: "Tipo de Documento" },
    { value: "documento_descripcion", label: "Descripción del Documento" },
    { value: "mensajero_nombre", label: "Mensajero" },
    { value: "es_urgente", label: "Urgente (Sí/No)" },
    { value: "tipo", label: "Tipo (entrega/recogida)" },
    { value: "notas", label: "Notas" },
    { value: "destino_nombre", label: "Nombre Destinatario" },
    { value: "destino_telefono", label: "Teléfono Destinatario" }
  ];

  const [formData, setFormData] = useState({
    cliente: "",
    cliente_id: "",
    direccion_envio: "",
    telefono_cliente: "",
    celular_cliente: "",
    envio_sucursal: false,
    sucursal: "",
    tipo_documento_id: "",
    documento_descripcion: "",
    mensajero_id: "",
    es_urgente: false,
    tipo: "entrega",
    notas: "",
    archivo_url: "",
    polizas_renovacion: []
  });

  // Pólizas RENOVADA disponibles del cliente seleccionado (para selección múltiple)
  const [polizasDisponibles, setPolizasDisponibles] = useState([]);
  const [loadingPolizas, setLoadingPolizas] = useState(false);

  // Estado para estadísticas por mensajero
  const [statsMensajeros, setStatsMensajeros] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  // Filtro por mensajero (click en tarjeta)
  const [mensajeroFilter, setMensajeroFilter] = useState(null); // { id, nombre, tipo: "en_proceso" | "completado" }

  // Sucursal grouping state
  const [expandedSucursales, setExpandedSucursales] = useState({});
  const [processingGrupo, setProcessingGrupo] = useState(null);

  // ¿Debemos incluir el histórico (completadas de meses anteriores)?
  // Se activa cuando el usuario busca o aplica cualquier filtro, de modo que
  // las solicitudes archivadas de la vista por defecto sigan siendo accesibles.
  const incluirHistorico = (
    (searchQuery || "").trim() !== "" ||
    estadoFilter !== "all" ||
    urgenteFilter !== "all" ||
    !!mensajeroFilter
  );
  const incluirHistoricoRef = useRef(incluirHistorico);
  incluirHistoricoRef.current = incluirHistorico;

  const fetchData = useCallback(async () => {
    try {
      const solUrl = `${API}/mensajeria/solicitudes${incluirHistoricoRef.current ? "?incluir_historico=true" : ""}`;
      const [solRes, mensRes, statsRes, tiposRes, settingsRes, clientesRes, statsMensRes] = await Promise.all([
        axios.get(solUrl, getAuthHeaders()),
        axios.get(`${API}/mensajeros`, getAuthHeaders()),
        axios.get(`${API}/mensajeria/stats`, getAuthHeaders()),
        axios.get(`${API}/mensajeria/tipos-documento`, getAuthHeaders()),
        axios.get(`${API}/app/settings`, getAuthHeaders()).catch(() => ({ data: { photo_required: false } })),
        axios.get(`${API}/mensajeria/clientes`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/mensajeria/stats-por-mensajero`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setSolicitudes(solRes.data);
      setMensajeros(mensRes.data);
      setStats(statsRes.data);
      setTiposDocumento(tiposRes.data);
      setAppSettings(settingsRes.data);
      setClientes(clientesRes.data);
      setStatsMensajeros(statsMensRes.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Recarga sólo la lista de solicitudes respetando incluir_historico.
  // Se usa al activar/desactivar búsqueda o filtros para que las solicitudes
  // archivadas (completadas de meses anteriores) aparezcan en los resultados.
  const fetchSolicitudesList = useCallback(async (incluirHist) => {
    try {
      const url = `${API}/mensajeria/solicitudes${incluirHist ? "?incluir_historico=true" : ""}`;
      const res = await axios.get(url, getAuthHeaders());
      setSolicitudes(res.data);
    } catch (err) {
      console.error("Error al recargar solicitudes:", err);
    }
  }, [getAuthHeaders]);

  // Función para actualizar manualmente
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Datos actualizados");
  };

  // ============= Reporte Mensual Handlers =============
  const fetchReporteMensualConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mensajeria/reporte-mensual/config`, getAuthHeaders());
      setReporteMensualConfig(data);
      setReporteMensualForm({
        activo: !!data.activo,
        dia_envio: data.dia_envio || 1,
        hora_envio: data.hora_envio || "09:00",
        emails_adicionales: data.emails_adicionales || [],
        incluir_admin_original: data.incluir_admin_original !== false,
      });
    } catch (e) {
      toast.error("No se pudo cargar la configuración del reporte");
    }
  }, [getAuthHeaders]);

  const handleOpenReporteMensual = () => {
    setShowReporteMensualDialog(true);
    fetchReporteMensualConfig();
    fetchReporteMensualHistorial();
  };

  const fetchReporteMensualHistorial = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mensajeria/reporte-mensual/historial?limit=30`, getAuthHeaders());
      setReporteMensualHistorial(data.items || []);
    } catch (e) {
      // silent fail — solo se muestra la sección si hay datos
    }
  }, [getAuthHeaders]);

  const handleResendReporteMensual = async (item) => {
    if (!window.confirm(`¿Reenviar el reporte de ${item.mes_nombre} a los destinatarios actualmente configurados?`)) return;
    try {
      setReporteMensualResendingId(item.id);
      const { data } = await axios.post(`${API}/mensajeria/reporte-mensual/historial/${item.id}/reenviar`, {}, getAuthHeaders());
      if (data.enviado) {
        const ok = (data.envio?.results || []).filter(r => r.ok).length;
        toast.success(`Reenviado ${item.mes_nombre} a ${ok}/${data.envio?.total || 0} destinatarios`);
        fetchReporteMensualHistorial();
      } else {
        toast.error(data.razon || "No se pudo reenviar");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reenviar");
    } finally {
      setReporteMensualResendingId(null);
    }
  };

  const handleSaveReporteMensualConfig = async () => {
    try {
      setReporteMensualSavingFlag(true);
      const { data } = await axios.put(`${API}/mensajeria/reporte-mensual/config`, reporteMensualForm, getAuthHeaders());
      setReporteMensualConfig(data);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setReporteMensualSavingFlag(false);
    }
  };

  const handleSendReporteMensualNow = async () => {
    try {
      setReporteMensualSendingFlag(true);
      const { data } = await axios.post(`${API}/mensajeria/reporte-mensual/enviar-ahora`, {}, getAuthHeaders());
      if (data.enviado) {
        const ok = (data.envio?.results || []).filter(r => r.ok).length;
        toast.success(`Reporte enviado a ${ok}/${data.envio?.total || 0} destinatarios`);
        fetchReporteMensualHistorial();
      } else {
        toast.error(data.razon || "No se envió el reporte");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar el reporte");
    } finally {
      setReporteMensualSendingFlag(false);
    }
  };

  const handlePreviewReporteMensual = async (formato) => {
    // formato: "pdf" | "xlsx"
    try {
      const url = `${API}/mensajeria/reporte-mensual/previsualizar-${formato}`;
      const { data } = await axios.get(url, { responseType: "blob", ...getAuthHeaders() });
      const blobUrl = URL.createObjectURL(data);
      if (formato === "pdf") {
        window.open(blobUrl, "_blank");
      } else {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `reporte_mensajeria.${formato}`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (e) {
      toast.error("Error generando el reporte");
    }
  };

  const handleAddReporteEmail = () => {
    const email = reporteMensualEmailInput.trim();
    if (!email || !email.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    if ((reporteMensualForm.emails_adicionales || []).includes(email)) {
      toast.error("Ya está agregado");
      return;
    }
    setReporteMensualForm({
      ...reporteMensualForm,
      emails_adicionales: [...(reporteMensualForm.emails_adicionales || []), email],
    });
    setReporteMensualEmailInput("");
  };

  const handleRemoveReporteEmail = (email) => {
    setReporteMensualForm({
      ...reporteMensualForm,
      emails_adicionales: (reporteMensualForm.emails_adicionales || []).filter(e => e !== email),
    });
  };


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Recargar la lista cuando se activa/desactiva el histórico (búsqueda/filtros).
  // Se omite la primera ejecución porque fetchData ya cargó la lista inicial.
  const incluirHistoricoMountRef = useRef(false);
  useEffect(() => {
    if (!incluirHistoricoMountRef.current) {
      incluirHistoricoMountRef.current = true;
      return;
    }
    fetchSolicitudesList(incluirHistorico);
  }, [incluirHistorico, fetchSolicitudesList]);

  // Auto-open solicitud from URL param (from email link)
  useEffect(() => {
    if (urlSolicitudId && solicitudes.length > 0) {
      const sol = solicitudes.find(s => s.id === urlSolicitudId);
      if (sol) {
        openViewDialog(sol);
      }
    }
  }, [urlSolicitudId, solicitudes]);

  // Reset to first page whenever the active filters / search / view change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, estadoFilter, urgenteFilter, mensajeroFilter, viewMode, pageSize]);


  const resetForm = () => {
    setFormData({
      cliente: user?.name || "",
      cliente_id: "",
      direccion_envio: "",
      telefono_cliente: "",
      celular_cliente: "",
      envio_sucursal: false,
      sucursal: "",
      tipo_documento_id: "",
      documento_descripcion: "",
      mensajero_id: "",
      es_urgente: false,
      tipo: "entrega",
      notas: "",
      archivo_url: "",
      polizas_renovacion: []
    });
    setEditingSolicitud(null);
    setSelectedFile(null);
    setTemplateFields([]);
    setCustomFieldsData({});
    setPolizasDisponibles([]);
  };

  // ---- Borrador de "Nueva Solicitud" (persistencia local por usuario) ----
  const getDraftKey = () => `mensajeria_nueva_solicitud_draft_${user?.id || 'anon'}`;

  const draftTieneContenido = (fd, cfd) => {
    if (!fd) return false;
    const camposTexto = ["cliente_id", "direccion_envio", "telefono_cliente", "celular_cliente",
      "tipo_documento_id", "documento_descripcion", "mensajero_id", "notas", "sucursal"];
    if (camposTexto.some((k) => fd[k] && String(fd[k]).trim() !== "")) return true;
    if (Array.isArray(fd.polizas_renovacion) && fd.polizas_renovacion.length > 0) return true;
    if (cfd && Object.values(cfd).some((v) => v && String(v).trim() !== "")) return true;
    return false;
  };

  const clearDraft = () => {
    try { localStorage.removeItem(getDraftKey()); } catch (_) { /* noop */ }
  };

  const discardDraft = () => {
    clearDraft();
    resetForm();
    toast.success("Borrador descartado");
  };

  // Autoguardado del borrador (solo al CREAR, no al editar)
  useEffect(() => {
    if (!showDialog || editingSolicitud) return;
    try {
      if (draftTieneContenido(formData, customFieldsData)) {
        localStorage.setItem(getDraftKey(), JSON.stringify({ formData, customFieldsData }));
      }
    } catch (_) { /* noop */ }
  }, [formData, customFieldsData, showDialog, editingSolicitud]);

  const openNewDialog = async () => {
    resetForm();
    let restored = false;
    try {
      const raw = localStorage.getItem(getDraftKey());
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft?.formData && draftTieneContenido(draft.formData, draft.customFieldsData)) {
          setFormData(draft.formData);
          restored = true;
          // Recargar los campos de plantilla del tipo de documento guardado (preservando valores)
          const tdid = draft.formData.tipo_documento_id;
          if (tdid && tdid !== "none") {
            try {
              const res = await axios.get(`${API}/plantillas-mensajeria/by-tipo/${tdid}`, getAuthHeaders());
              if (res.data?.fields?.length) setTemplateFields(res.data.fields);
            } catch (_) { /* noop */ }
          }
          if (draft.customFieldsData) setCustomFieldsData(draft.customFieldsData);
        }
      }
    } catch (_) { /* noop */ }
    if (restored) toast.info("Se restauró tu borrador anterior");
    setShowDialog(true);
  };

  const openEditDialog = (solicitud) => {
    setEditingSolicitud(solicitud);
    setFormData({
      cliente: solicitud.cliente,
      cliente_id: solicitud.cliente_id || "",
      direccion_envio: solicitud.direccion_envio,
      telefono_cliente: solicitud.telefono_cliente || "",
      celular_cliente: solicitud.celular_cliente || "",
      envio_sucursal: solicitud.envio_sucursal || false,
      sucursal: solicitud.sucursal || "",
      tipo_documento_id: solicitud.tipo_documento_id || "",
      documento_descripcion: solicitud.documento_descripcion || "",
      mensajero_id: solicitud.mensajero_id || "",
      es_urgente: solicitud.es_urgente,
      tipo: solicitud.tipo,
      notas: solicitud.notas || "",
      archivo_url: solicitud.archivo_url || "",
      polizas_renovacion: solicitud.polizas_renovacion || []
    });
    setShowDialog(true);
  };

  // Handle cliente selection from dropdown
  const handleClienteSelect = (cliente) => {
    // Check if cliente has a sucursal assigned (not N/A)
    const hasSucursal = cliente.sucursal && cliente.sucursal !== "N/A";
    
    setFormData(prev => ({
      ...prev,
      cliente: cliente.nombre,
      cliente_id: cliente.id,
      direccion_envio: cliente.direccion || prev.direccion_envio,
      telefono_cliente: cliente.telefono || "",
      celular_cliente: cliente.celular || "",
      envio_sucursal: hasSucursal,
      sucursal: hasSucursal ? cliente.sucursal : ""
    }));
    setClienteSearchOpen(false);
  };

  // Buscar pólizas RENOVADA del cliente seleccionado (selección múltiple).
  const fetchPolizasRenovadas = useCallback(async (clienteNombre) => {
    const nombre = (clienteNombre || "").trim();
    if (!nombre) {
      setPolizasDisponibles([]);
      return;
    }
    setLoadingPolizas(true);
    try {
      const res = await axios.get(
        `${API}/control-renovaciones/polizas-renovadas`,
        { ...getAuthHeaders(), params: { cliente: nombre } }
      );
      setPolizasDisponibles(res.data || []);
    } catch (err) {
      setPolizasDisponibles([]);
    } finally {
      setLoadingPolizas(false);
    }
  }, []);

  // Recargar pólizas cuando cambia el cliente con el diálogo abierto (debounce).
  useEffect(() => {
    if (!showDialog) return;
    const t = setTimeout(() => fetchPolizasRenovadas(formData.cliente), 400);
    return () => clearTimeout(t);
  }, [formData.cliente, showDialog, fetchPolizasRenovadas]);

  // Alternar selección de una póliza de renovación.
  const togglePolizaRenovacion = (poliza) => {
    setFormData(prev => {
      const exists = (prev.polizas_renovacion || []).some(p => p.renovacion_id === poliza.renovacion_id);
      const polizas_renovacion = exists
        ? prev.polizas_renovacion.filter(p => p.renovacion_id !== poliza.renovacion_id)
        : [...(prev.polizas_renovacion || []), {
            renovacion_id: poliza.renovacion_id,
            poliza: poliza.poliza,
            cliente: poliza.cliente,
            ramo: poliza.ramo,
            vence_en: poliza.vence_en,
          }];
      return { ...prev, polizas_renovacion };
    });
  };
  // Create new cliente and select it
  const handleCreateNewCliente = async () => {
    if (!newClienteData.nombre.trim()) {
      toast.error("El nombre del cliente es requerido");
      return;
    }
    try {
      const res = await axios.post(`${API}/mensajeria/clientes`, newClienteData, getAuthHeaders());
      const newCliente = res.data;
      setClientes(prev => [...prev, newCliente]);
      handleClienteSelect(newCliente);
      setShowNewClienteDialog(false);
      setNewClienteData({ nombre: "", direccion: "", telefono: "", celular: "", sucursal: "N/A" });
      toast.success("Cliente creado y seleccionado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear cliente");
    }
  };

  const handleTipoDocumentoChange = async (tipoDocumentoId) => {
    setFormData(prev => ({ ...prev, tipo_documento_id: tipoDocumentoId }));
    
    // Reset custom fields when type changes
    setTemplateFields([]);
    setCustomFieldsData({});
    
    // Fetch template for this document type
    if (tipoDocumentoId && tipoDocumentoId !== "none") {
      setLoadingPlantilla(true);
      try {
        const res = await axios.get(`${API}/plantillas-mensajeria/by-tipo/${tipoDocumentoId}`, getAuthHeaders());
        if (res.data) {
          setFormData(prev => ({
            ...prev,
            documento_descripcion: res.data.contenido || prev.documento_descripcion
          }));
          
          // Load custom fields from template
          if (res.data.fields && res.data.fields.length > 0) {
            setTemplateFields(res.data.fields);
            // Initialize custom fields data with defaults and auto-fill user data
            const initialData = {};
            res.data.fields.forEach(field => {
              const fieldKey = field.name || field.id;
              const fieldNameLower = fieldKey.toLowerCase();
              
              // Auto-fill user name for fields that represent the requester/solicitor
              if (
                fieldNameLower === 'nombre' || 
                fieldNameLower === 'solicitante' || 
                fieldNameLower === 'nombre_solicitante' ||
                fieldNameLower === 'nombre_empleado' ||
                fieldNameLower === 'empleado' ||
                fieldNameLower === 'remitente'
              ) {
                initialData[fieldKey] = user?.name || user?.email?.split('@')[0] || "";
              } 
              // Auto-fill department for department fields
              else if (
                fieldNameLower === 'departamento' ||
                fieldNameLower === 'departamento_id' ||
                fieldNameLower === 'department' ||
                fieldNameLower === 'area' ||
                fieldNameLower === 'área'
              ) {
                initialData[fieldKey] = user?.departamento_nombre || "";
              }
              else {
                initialData[fieldKey] = field.default_value || "";
              }
            });
            setCustomFieldsData(initialData);
            toast.success(`Plantilla cargada con ${res.data.fields.length} campo(s) personalizado(s)`);
          } else {
            toast.success("Plantilla cargada automáticamente");
          }
        }
      } catch (err) {
        // No template found, that's okay
        console.log("No template found for this document type");
      } finally {
        setLoadingPlantilla(false);
      }
    }
  };

  // Handler for custom field value changes
  const handleCustomFieldChange = (fieldKey, value) => {
    setCustomFieldsData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Render a custom field based on its type
  const renderCustomField = (field) => {
    const fieldKey = field.name || field.id;
    const value = customFieldsData[fieldKey] || "";
    const widthClass = field.column_width === "half" ? "col-span-1" : 
                       field.column_width === "third" ? "col-span-1" : "col-span-2";

    switch (field.field_type) {
      case "textarea":
        return (
          <div key={fieldKey} className={widthClass}>
            <Label className="flex items-center gap-1 dark:text-slate-300">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value)}
              placeholder={field.label}
              className="mt-1 min-h-[80px] dark:bg-slate-700 dark:border-slate-600"
              data-testid={`custom-field-${fieldKey}`}
            />
          </div>
        );
      case "number":
        return (
          <div key={fieldKey} className={widthClass}>
            <Label className="flex items-center gap-1 dark:text-slate-300">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type="number"
              value={value}
              onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value)}
              placeholder={field.label}
              className="mt-1 dark:bg-slate-700 dark:border-slate-600"
              data-testid={`custom-field-${fieldKey}`}
            />
          </div>
        );
      case "date":
        return (
          <div key={fieldKey} className={widthClass}>
            <Label className="flex items-center gap-1 dark:text-slate-300">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type="date"
              value={value}
              onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value)}
              className="mt-1 dark:bg-slate-700 dark:border-slate-600"
              data-testid={`custom-field-${fieldKey}`}
            />
          </div>
        );
      case "select":
        return (
          <div key={fieldKey} className={widthClass}>
            <Label className="flex items-center gap-1 dark:text-slate-300">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Select value={value || "none"} onValueChange={(v) => handleCustomFieldChange(fieldKey, v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600" data-testid={`custom-field-${fieldKey}`}>
                <SelectValue placeholder={`Seleccione ${field.label}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleccionar...</SelectItem>
                {(field.options || []).map((opt, idx) => (
                  <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "checkbox":
        return (
          <div key={fieldKey} className={`${widthClass} flex items-center gap-3 pt-6`}>
            <Checkbox
              id={fieldKey}
              checked={value === "true" || value === true}
              onCheckedChange={(checked) => handleCustomFieldChange(fieldKey, checked)}
              data-testid={`custom-field-${fieldKey}`}
            />
            <Label htmlFor={fieldKey} className="flex items-center gap-1 cursor-pointer dark:text-slate-300">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
          </div>
        );
      default: // text
        return (
          <div key={fieldKey} className={widthClass}>
            <Label className="flex items-center gap-1 dark:text-slate-300">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={value}
              onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value)}
              placeholder={field.label}
              className="mt-1 dark:bg-slate-700 dark:border-slate-600"
              data-testid={`custom-field-${fieldKey}`}
            />
          </div>
        );
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Solo se permiten archivos PDF, JPEG o PNG");
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("El archivo no debe superar 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.cliente || !formData.direccion_envio) {
      toast.error("Complete los campos obligatorios");
      return;
    }

    // Validate required custom fields
    const missingRequiredFields = templateFields
      .filter(f => f.required)
      .filter(f => {
        const fieldKey = f.name || f.id;
        const value = customFieldsData[fieldKey];
        return !value || value === "" || value === "none";
      });
    
    if (missingRequiredFields.length > 0) {
      toast.error(`Campos personalizados requeridos: ${missingRequiredFields.map(f => f.label).join(', ')}`);
      return;
    }

    try {
      let solicitudId = editingSolicitud?.id;
      
      // Include custom fields data in the request
      const solicitudData = {
        ...formData,
        custom_fields_data: Object.keys(customFieldsData).length > 0 ? customFieldsData : null
      };
      
      if (editingSolicitud) {
        await axios.put(`${API}/mensajeria/solicitudes/${editingSolicitud.id}`, solicitudData, getAuthHeaders());
        toast.success("Solicitud actualizada");
      } else {
        const response = await axios.post(`${API}/mensajeria/solicitudes`, solicitudData, getAuthHeaders());
        solicitudId = response.data.id;
        toast.success("Solicitud creada");
      }

      // Upload file if selected
      if (selectedFile && solicitudId) {
        setUploadingFile(true);
        const fileFormData = new FormData();
        fileFormData.append('file', selectedFile);
        
        const token = localStorage.getItem("token");
        await axios.post(
          `${API}/mensajeria/solicitudes/${solicitudId}/upload`,
          fileFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        toast.success("Archivo adjuntado correctamente");
      }

      setShowDialog(false);
      clearDraft();
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setUploadingFile(false);
    }
  };

  // Check if user can view all mensajeria (admin, mensajero, recepcionista, or has special permission)
  const canViewAllMensajeria = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    // Check special permission first
    if (user?.can_view_all_mensajeria === true) {
      return true;
    }
    // Admin and mensajero can always view all
    return ["administrador", "admin", "mensajero", "recepcion", "recepcionista"].includes(role);
  };

  // Check if user can change status (admin, mensajero - NOT recepcionista, NOT view-only users)
  const canChangeStatus = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    // Recepcionista can only view, not change status
    if (role === "recepcion" || role === "recepcionista") {
      return false;
    }
    // Users with only view permission cannot change status
    if (user?.can_view_all_mensajeria === true && !["administrador", "admin", "mensajero"].includes(role)) {
      return false;
    }
    return ["administrador", "admin", "mensajero"].includes(role);
  };

  // Check if user is Recepcionista or has view-only permission (read-only mode - can view all but not edit)
  const isViewOnlyUser = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    // Recepcionista is always view-only
    if (role === "recepcion" || role === "recepcionista") {
      return true;
    }
    // Users with can_view_all_mensajeria but not admin/mensajero role are view-only
    if (user?.can_view_all_mensajeria === true && !["administrador", "admin", "mensajero"].includes(role)) {
      return true;
    }
    return false;
  };

  // Legacy function for backwards compatibility
  const isRecepcionista = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    return role === "recepcion" || role === "recepcionista";
  };

  // Check if user can edit/delete (admin, mensajero, or own records - NOT recepcionista, NOT view-only)
  // If `sol` is passed, additionally allows regular users to edit THEIR OWN records,
  // but only when the solicitud is still in state "pendiente" OR was created in the last 15 min.
  const canEdit = (sol = null) => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    // View-only users cannot edit
    if (isViewOnlyUser()) {
      return false;
    }
    // Admins / mensajeros can edit anything
    if (["administrador", "admin", "mensajero", "superadmin"].includes(role)) {
      return true;
    }
    // Regular users can edit their OWN records within the edit window
    if (sol && sol.user_id && user?.id && sol.user_id === user.id) {
      const estado = (sol.estado || "").toLowerCase();
      if (estado === "pendiente") return true;
      // Allow if created within last 15 minutes
      const created = sol.fecha_creacion || sol.created_at;
      if (created) {
        const createdAt = new Date(created).getTime();
        if (!isNaN(createdAt) && (Date.now() - createdAt) <= 15 * 60 * 1000) {
          return true;
        }
      }
    }
    return false;
  };

  // Check if user can create new solicitudes
  const canCreate = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    // View-only users cannot create
    if (isViewOnlyUser()) {
      return false;
    }
    return true;
  };

  const updateEstado = async (id, estado, fecha_entrega = null, soporte_url = null) => {
    try {
      let url = `${API}/mensajeria/solicitudes/${id}/estado?estado=${estado}`;
      if (fecha_entrega) {
        url += `&fecha_entrega=${fecha_entrega}`;
      }
      if (soporte_url) {
        url += `&soporte_entrega_url=${encodeURIComponent(soporte_url)}`;
      }
      await axios.put(url, {}, getAuthHeaders());
      toast.success(`Estado actualizado a ${estado}`);
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al actualizar estado";
      toast.error(errorMsg);
    }
  };

  // Handler for clicking "Completado" button - opens confirmation dialog
  const handleCompletadoClick = (solicitud) => {
    setSelectedSolicitudForEntrega(solicitud);
    setEntregaData({
      fecha_entrega: new Date().toISOString().split('T')[0],
      soporte_file: null
    });
    setShowEntregaDialog(true);
  };

  // Handler for confirming delivery
  const handleConfirmEntrega = async () => {
    if (!entregaData.fecha_entrega) {
      toast.error("La fecha de entrega es obligatoria");
      return;
    }
    
    // Validate photo is required based on app settings
    if (appSettings.photo_required && !entregaData.soporte_file) {
      toast.error("La foto de soporte es obligatoria para confirmar la entrega");
      return;
    }

    setUploadingSoporte(true);
    try {
      let soporteUrl = null;
      
      // Upload soporte file if provided
      if (entregaData.soporte_file) {
        const formData = new FormData();
        formData.append('file', entregaData.soporte_file);
        const uploadRes = await axios.post(
          `${API}/mensajeria/solicitudes/${selectedSolicitudForEntrega.id}/upload-soporte`,
          formData,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        soporteUrl = uploadRes.data.url;
      }

      // Update status to "completado"
      await updateEstado(selectedSolicitudForEntrega.id, "completado", entregaData.fecha_entrega, soporteUrl);
      setShowEntregaDialog(false);
      setSelectedSolicitudForEntrega(null);
      setEntregaData({ fecha_entrega: "", soporte_file: null });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al confirmar entrega";
      toast.error(errorMsg);
    } finally {
      setUploadingSoporte(false);
    }
  };

  const deleteSolicitud = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta solicitud?")) return;
    try {
      await axios.delete(`${API}/mensajeria/solicitudes/${id}`, getAuthHeaders());
      toast.success("Solicitud eliminada");
      setSelectedItems(prev => prev.filter(itemId => itemId !== id));
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Bulk selection functions
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredSolicitudes.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredSolicitudes.map(s => s.id));
    }
  };

  const deleteBulkSolicitudes = async () => {
    if (selectedItems.length === 0) {
      toast.error("No hay solicitudes seleccionadas");
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de eliminar ${selectedItems.length} solicitud(es)?`)) return;
    
    setDeletingBulk(true);
    try {
      // Delete all selected items
      await Promise.all(
        selectedItems.map(id => 
          axios.delete(`${API}/mensajeria/solicitudes/${id}`, getAuthHeaders())
        )
      );
      toast.success(`${selectedItems.length} solicitud(es) eliminada(s)`);
      setSelectedItems([]);
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar algunas solicitudes");
      fetchData();
    } finally {
      setDeletingBulk(false);
    }
  };

  const openViewDialog = async (solicitud) => {
    try {
      const res = await axios.get(`${API}/mensajeria/solicitudes/${solicitud.id}`, getAuthHeaders());
      setSelectedSolicitud(res.data);
      setShowViewDialog(true);
    } catch (err) {
      toast.error("Error al cargar la solicitud");
    }
  };

  // Función para eliminar imagen de entrega (solo admin)
  const deleteDeliveryImage = async (assetId, imageType) => {
    if (!window.confirm(`¿Estás seguro de eliminar esta ${imageType === 'photo' ? 'foto' : 'firma'}? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/entregas/imagen/${assetId}`, getAuthHeaders());
      toast.success(`${imageType === 'photo' ? 'Foto' : 'Firma'} eliminada correctamente`);
      
      // Recargar la solicitud para actualizar la vista
      if (selectedSolicitud) {
        const res = await axios.get(`${API}/mensajeria/solicitudes/${selectedSolicitud.id}`, getAuthHeaders());
        setSelectedSolicitud(res.data);
      }
      fetchData();
    } catch (err) {
      console.error("Error deleting image:", err);
      toast.error(err.response?.data?.detail || "Error al eliminar la imagen");
    }
  };

  // Verificar si el usuario es admin
  const isAdmin = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    return ["administrador", "admin"].includes(role);
  };

  // Verificar si puede ver las tarjetas de mensajeros (admin, supervisor, recepcion)
  const canViewMensajerosStats = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    return ["administrador", "admin", "supervisor", "supervisora", "recepcion", "recepcionista"].includes(role);
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      pendiente: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      en_proceso: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      completado: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      cancelado: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      devuelto: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
    };
    const labels = {
      pendiente: "Pendiente",
      en_proceso: "En Proceso",
      completado: "Completado",
      cancelado: "Cancelado",
      devuelto: "Devuelto"
    };
    return <Badge className={`${styles[estado]} border-0`}>{labels[estado]}</Badge>;
  };

  const filteredSolicitudes = solicitudes.filter(s => {
    const matchesSearch = 
      s.cliente?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.documento_descripcion?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.tipo_documento_nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.direccion_envio?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEstado = estadoFilter === "all" || s.estado === estadoFilter;
    const matchesUrgente = urgenteFilter === "all" || 
      (urgenteFilter === "urgente" && s.es_urgente) ||
      (urgenteFilter === "normal" && !s.es_urgente);
    const matchesMensajero = !mensajeroFilter || (
      (s.mensajero_name || "").toLowerCase() === (mensajeroFilter.nombre || "").toLowerCase()
    );
    return matchesSearch && matchesEstado && matchesUrgente && matchesMensajero;
  });

  // Exportar a Excel SOLO lo que se ve actualmente en la grilla (filteredSolicitudes).
  // Respeta todos los filtros activos: búsqueda, estado, urgencia, mensajero.
  const handleExportExcelGrilla = () => {
    if (!filteredSolicitudes || filteredSolicitudes.length === 0) {
      toast.error("No hay solicitudes que exportar con los filtros actuales");
      return;
    }
    const labelEstado = {
      pendiente: "Pendiente",
      en_proceso: "En Proceso",
      completado: "Completado",
      devuelto: "Devuelto",
    };
    const filaFmt = (s) => ({
      "Cliente": s.cliente || "",
      "Sucursal": s.sucursal_nombre || "",
      "Tipo Documento": s.tipo_documento_nombre || "",
      "Descripción Documento": s.documento_descripcion || "",
      "Dirección de Envío": s.direccion_envio || "",
      "Teléfono Destinatario": s.telefono_destinatario || "",
      "Persona Contacto": s.persona_contacto || "",
      "Estado": labelEstado[s.estado] || s.estado || "",
      "Urgente": s.es_urgente ? "Sí" : "No",
      "Mensajero Asignado": s.mensajero_name || "",
      "Mensajero Anterior (si fue devuelta)": s.mensajero_anterior_nombre || "",
      "Motivo de Devolución": s.motivo_devolucion || "",
      "Fecha Solicitud (Mostrada)": s.created_at ? new Date(s.created_at).toLocaleString("es-DO") : "",
      "Fecha Creación Original (si reenviada)": s.fecha_creacion_original ? new Date(s.fecha_creacion_original).toLocaleString("es-DO") : "",
      "Fecha de Asignación": s.fecha_asignacion ? new Date(s.fecha_asignacion).toLocaleString("es-DO") : "",
      "Fecha de Entrega": s.fecha_entrega ? new Date(s.fecha_entrega).toLocaleString("es-DO") : "",
      "Fecha Devuelta": s.fecha_devuelto ? new Date(s.fecha_devuelto).toLocaleString("es-DO") : "",
      "Fecha Reenvío": s.fecha_reenvio ? new Date(s.fecha_reenvio).toLocaleString("es-DO") : "",
      "Solicitante": s.solicitante_nombre || s.solicitante_id || "",
      "Observaciones": s.observaciones || "",
    });
    const rows = filteredSolicitudes.map(filaFmt);
    const ws = XLSX.utils.json_to_sheet(rows);
    // Ajustar ancho de columnas básico
    const colWidths = Object.keys(rows[0]).map((k) => ({
      wch: Math.min(Math.max(k.length, ...rows.map((r) => String(r[k] || "").length)) + 2, 50),
    }));
    ws["!cols"] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mensajería");
    const fecha = new Date().toISOString().slice(0, 10);
    const filtrosTxt = [];
    if (estadoFilter !== "all") filtrosTxt.push(estadoFilter);
    if (urgenteFilter !== "all") filtrosTxt.push(urgenteFilter);
    if (mensajeroFilter) filtrosTxt.push((mensajeroFilter.nombre || "").replace(/\s+/g, "_"));
    if (searchQuery) filtrosTxt.push("busqueda");
    const sufijo = filtrosTxt.length ? `_${filtrosTxt.join("-")}` : "";
    const filename = `mensajeria_${fecha}${sufijo}_${rows.length}registros.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(`${rows.length} solicitud(es) exportada(s) a ${filename}`);
  };

  // Group solicitudes by sucursal
  const { sucursalGroups, individualSolicitudes } = (() => {
    const groups = {};
    const individual = [];
    filteredSolicitudes.forEach(sol => {
      if (sol.envio_sucursal && sol.sucursal && sol.estado !== "completado" && sol.estado !== "cancelado") {
        if (!groups[sol.sucursal]) {
          groups[sol.sucursal] = [];
        }
        groups[sol.sucursal].push(sol);
      } else {
        individual.push(sol);
      }
    });
    return {
      sucursalGroups: Object.entries(groups).map(([sucursal, items]) => ({
        sucursal,
        items,
        count: items.length,
        pendientes: items.filter(i => i.estado === "pendiente").length,
        enProceso: items.filter(i => i.estado === "en_proceso").length,
        devueltos: items.filter(i => i.estado === "devuelto").length,
        dominantEstado: items.every(i => i.estado === "en_proceso") ? "en_proceso" 
          : items.every(i => i.estado === "pendiente") ? "pendiente" 
          : "mixto"
      })),
      individualSolicitudes: individual
    };
  })();

  // ---- Client-side pagination derived values ----
  // Table view paginates the "individual" rows (sucursal groups stay pinned on top);
  // the other views paginate the full filtered list.
  const pageBaseCount = viewMode === 'table' ? individualSolicitudes.length : filteredSolicitudes.length;
  const totalPages = Math.max(1, Math.ceil(pageBaseCount / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const pagedFiltered = filteredSolicitudes.slice(pageStart, pageEnd);
  const pagedIndividual = individualSolicitudes.slice(pageStart, pageEnd);

  const toggleSucursalExpanded = (sucursal) => {
    setExpandedSucursales(prev => ({ ...prev, [sucursal]: !prev[sucursal] }));
  };

  const updateGrupoEstado = async (sucursal, estado) => {
    setProcessingGrupo(sucursal);
    try {
      let url = `${API}/mensajeria/grupo-sucursal/estado?sucursal=${encodeURIComponent(sucursal)}&estado=${estado}`;
      await axios.put(url, {}, getAuthHeaders());
      toast.success(`Grupo ${sucursal}: ${estado === "en_proceso" ? "iniciado" : "actualizado"}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar grupo");
    } finally {
      setProcessingGrupo(null);
    }
  };

  // State for group delivery confirmation
  const [showGrupoEntregaDialog, setShowGrupoEntregaDialog] = useState(false);
  const [selectedGrupoForEntrega, setSelectedGrupoForEntrega] = useState(null);
  const [grupoEntregaData, setGrupoEntregaData] = useState({ fecha_entrega: "", soporte_file: null });
  const [uploadingGrupoSoporte, setUploadingGrupoSoporte] = useState(false);
  const grupoSoporteInputRef = useRef(null);

  const handleGrupoCompletarClick = (grupo) => {
    setSelectedGrupoForEntrega(grupo);
    setGrupoEntregaData({ fecha_entrega: new Date().toISOString().split('T')[0], soporte_file: null });
    setShowGrupoEntregaDialog(true);
  };

  const handleConfirmGrupoEntrega = async () => {
    if (!grupoEntregaData.fecha_entrega) {
      toast.error("La fecha de entrega es obligatoria");
      return;
    }
    setUploadingGrupoSoporte(true);
    try {
      let soporteUrl = null;
      if (grupoEntregaData.soporte_file) {
        const fd = new FormData();
        fd.append('file', grupoEntregaData.soporte_file);
        const uploadRes = await axios.post(
          `${API}/mensajeria/grupo-sucursal/${encodeURIComponent(selectedGrupoForEntrega.sucursal)}/upload-soporte`,
          fd,
          { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
        );
        soporteUrl = uploadRes.data.url;
      }
      let url = `${API}/mensajeria/grupo-sucursal/estado?sucursal=${encodeURIComponent(selectedGrupoForEntrega.sucursal)}&estado=completado&fecha_entrega=${grupoEntregaData.fecha_entrega}`;
      if (soporteUrl) url += `&soporte_entrega_url=${encodeURIComponent(soporteUrl)}`;
      await axios.put(url, {}, getAuthHeaders());
      toast.success(`Grupo ${selectedGrupoForEntrega.sucursal} completado`);
      setShowGrupoEntregaDialog(false);
      setSelectedGrupoForEntrega(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al completar grupo");
    } finally {
      setUploadingGrupoSoporte(false);
    }
  };

  const downloadArchivoAdjunto = async (solicitud) => {
    if (solicitud.archivo_url) {
      try {
        // Use the new endpoint to download the file
        const response = await axios.get(
          `${API}/mensajeria/solicitudes/${solicitud.id}/archivo`,
          {
            ...getAuthHeaders(),
            responseType: 'blob'
          }
        );
        
        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = solicitud.archivo_nombre || 'documento';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Error downloading file:", err);
        toast.error("Error al descargar el archivo");
      }
    }
  };

  const viewArchivoAdjunto = async (solicitud) => {
    if (solicitud.archivo_url) {
      try {
        // Fetch the file with authentication
        const response = await axios.get(
          `${API}/mensajeria/solicitudes/${solicitud.id}/archivo?inline=true`,
          {
            ...getAuthHeaders(),
            responseType: 'blob'
          }
        );
        
        // Create blob URL and open in new tab
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Clean up after a delay (to allow the new tab to load)
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (err) {
        console.error("Error viewing file:", err);
        toast.error("Error al ver el archivo");
      }
    }
  };

  // Bulk Import Functions
  const openBulkDialog = () => {
    setBulkStep(1);
    setBulkPreview(null);
    setBulkMapping({});
    setBulkResult(null);
    setShowBulkDialog(true);
  };

  const handleBulkFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBulkLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/mensajeria/solicitudes/bulk-import/preview`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBulkPreview(res.data);
      setBulkMapping(res.data.column_mapping || {});
      setBulkStep(2);
      toast.success(`${res.data.total_rows} filas encontradas. La IA ha mapeado los campos automáticamente.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el archivo");
    } finally {
      setBulkLoading(false);
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    }
  };

  const updateBulkMapping = (excelCol, solicitudField) => {
    setBulkMapping(prev => {
      const newMapping = { ...prev };
      if (solicitudField) {
        newMapping[excelCol] = solicitudField;
      } else {
        delete newMapping[excelCol];
      }
      return newMapping;
    });
  };

  const executeBulkImport = async () => {
    if (!bulkPreview) return;

    // Validate required mappings
    const hasClienteMapping = Object.values(bulkMapping).includes('cliente');
    const hasDireccionMapping = Object.values(bulkMapping).includes('direccion_envio');
    
    if (!hasClienteMapping) {
      toast.error("Debe mapear una columna al campo 'Cliente'");
      return;
    }
    if (!hasDireccionMapping) {
      toast.error("Debe mapear una columna al campo 'Dirección de Envío'");
      return;
    }

    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/mensajeria/solicitudes/bulk-import/execute`, {
        column_mapping: bulkMapping,
        default_tipo: bulkPreview.default_tipo || "entrega",
        default_urgente: bulkPreview.default_urgente || false,
        rows: bulkPreview.all_data
      }, getAuthHeaders());

      setBulkResult(res.data);
      setBulkStep(3);
      
      if (res.data.created > 0) {
        toast.success(`${res.data.created} solicitudes creadas exitosamente`);
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar solicitudes");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-0 md:pt-0 overflow-x-clip" data-testid="solicitudes-mensajeria-page">
        {/* STICKY HEADER - se mantiene fijo al hacer scroll */}
        <div className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900 -mx-4 md:-mx-8 px-4 md:px-8 pt-14 md:pt-8 pb-4 border-b border-slate-200 dark:border-slate-700" data-testid="mensajeria-sticky-header">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Solicitudes de Mensajería
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {isRecepcionista() 
                ? "Vista de todas las correspondencias (solo lectura)"
                : "Gestiona las entregas y recogidas de documentos"
              }
            </p>
          </div>
          <div className="flex gap-3">
            {/* Botón de actualización */}
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-300 hover:bg-slate-100"
              title="Actualizar datos"
              data-testid="refresh-button"
            >
              <RotateCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>

            {/* Exportar Excel — exporta solo lo que se ve en la grilla (con filtros aplicados) */}
            <Button
              variant="outline"
              onClick={handleExportExcelGrilla}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="exportar-excel-grilla-btn"
              title="Exporta a Excel solo las solicitudes que se muestran actualmente en la grilla (con los filtros aplicados)"
            >
              <FileBarChart className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>

            {/* Reporte Mensual Button — admins only */}
            {isAdmin() && (
              <Button
                variant="outline"
                onClick={handleOpenReporteMensual}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="reporte-mensual-btn"
              >
                <FileBarChart className="w-4 h-4 mr-2" />
                Reporte Mensual
              </Button>
            )}

            {/* Diagnóstico de imágenes — admins only */}
            {isAdmin() && (
              <Button
                variant="outline"
                onClick={() => setShowDiagnostico(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="diagnostico-imagenes-btn"
                title="Verifica el estado de las imágenes de acuses (firmas y fotos de entrega)"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Diagnóstico imágenes
              </Button>
            )}

            {/* Reparar imágenes perdidas — admins only */}
            {isAdmin() && (
              <Button
                variant="outline"
                onClick={handleRepararBinarios}
                disabled={reparando}
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                data-testid="reparar-binarios-btn"
                title="Recupera imágenes perdidas leyendo del filesystem y re-adjuntándolas a la BD"
              >
                {reparando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Reparar imágenes
              </Button>
            )}

            {/* Verificar Imagen — admins only — pegás URL/ID y te dice por qué falla */}
            {isAdmin() && (
              <Button
                variant="outline"
                onClick={() => { setShowVerificarImagen(true); setVerificarResultado(null); setVerificarUrl(""); }}
                className="border-sky-400 text-sky-700 hover:bg-sky-50"
                data-testid="verificar-imagen-btn"
                title="Pegá una URL o asset_id de una imagen rota y este botón diagnostica qué pasa"
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                Verificar Imagen
              </Button>
            )}
            
            {/* Read-only badge for Recepcionista */}
            {isRecepcionista() && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 self-center">
                <Eye className="w-3 h-3 mr-1" />
                Solo Lectura
              </Badge>
            )}
            
            {canCreate() && (
              <>
                <Button 
                  onClick={openBulkDialog}
                  variant="outline"
                  className="border-[#0C314C] text-[#0C314C] hover:bg-[#0C314C]/10"
                  data-testid="bulk-import-button"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
                <Button 
                  onClick={openNewDialog}
                  className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  data-testid="new-solicitud-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Solicitud
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats - Clickable filters */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${estadoFilter === "all" && urgenteFilter === "all" && !searchQuery ? "ring-2 ring-blue-500" : ""}`}
            onClick={() => { setEstadoFilter("all"); setUrgenteFilter("all"); setSearchQuery(""); setMensajeroFilter(null); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${estadoFilter === "pendiente" && urgenteFilter === "all" ? "ring-2 ring-yellow-500" : ""}`}
            onClick={() => { setEstadoFilter("pendiente"); setUrgenteFilter("all"); setSearchQuery(""); setMensajeroFilter(null); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.pendientes || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${estadoFilter === "en_proceso" && urgenteFilter === "all" ? "ring-2 ring-blue-500" : ""}`}
            onClick={() => { setEstadoFilter("en_proceso"); setUrgenteFilter("all"); setSearchQuery(""); setMensajeroFilter(null); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.en_proceso || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">En Proceso</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${estadoFilter === "completado" && urgenteFilter === "all" ? "ring-2 ring-green-500" : ""}`}
            onClick={() => { setEstadoFilter("completado"); setUrgenteFilter("all"); setSearchQuery(""); setMensajeroFilter(null); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.completados || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Completados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${estadoFilter === "devuelto" && urgenteFilter === "all" ? "ring-2 ring-orange-500" : ""}`}
            onClick={() => { setEstadoFilter("devuelto"); setUrgenteFilter("all"); setSearchQuery(""); setMensajeroFilter(null); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.devueltos || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Devueltos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${urgenteFilter === "urgente" ? "ring-2 ring-red-500" : ""}`}
            onClick={() => { setUrgenteFilter("urgente"); setEstadoFilter("all"); setSearchQuery(""); setMensajeroFilter(null); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.urgentes_pendientes || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Urgentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tarjetas individuales por mensajero - Solo para admin, supervisores y recepción */}
        {canViewMensajerosStats() && statsMensajeros.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
            {statsMensajeros.map((mensajero) => (
              <Card 
                key={mensajero.id} 
                className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 transition-all ${mensajeroFilter?.id === mensajero.id ? "ring-2 ring-purple-500" : ""}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {mensajero.nombre}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Mensajero</p>
                    </div>
                    {mensajeroFilter?.id === mensajero.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                        onClick={(e) => { e.stopPropagation(); setMensajeroFilter(null); }}
                        data-testid={`clear-mensajero-filter-${mensajero.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div 
                      className={`bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 text-center cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors ${mensajeroFilter?.id === mensajero.id && mensajeroFilter?.tipo === "en_proceso" ? "ring-2 ring-amber-400" : ""}`}
                      onClick={() => {
                        if (mensajeroFilter?.id === mensajero.id && mensajeroFilter?.tipo === "en_proceso") {
                          setMensajeroFilter(null);
                          setEstadoFilter("all");
                        } else {
                          setMensajeroFilter({ id: mensajero.id, nombre: mensajero.nombre, tipo: "en_proceso" });
                          setEstadoFilter("en_proceso");
                          setUrgenteFilter("all");
                          setSearchQuery("");
                        }
                      }}
                      data-testid={`mensajero-${mensajero.id}-en-proceso`}
                    >
                      <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{mensajero.en_proceso || 0}</p>
                      <p className="text-xs text-amber-600/70 dark:text-amber-400/70">En Proceso</p>
                    </div>
                    <div 
                      className={`bg-green-50 dark:bg-green-900/20 rounded-lg p-2 text-center cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors ${mensajeroFilter?.id === mensajero.id && mensajeroFilter?.tipo === "completado" ? "ring-2 ring-green-400" : ""}`}
                      onClick={() => {
                        if (mensajeroFilter?.id === mensajero.id && mensajeroFilter?.tipo === "completado") {
                          setMensajeroFilter(null);
                          setEstadoFilter("all");
                        } else {
                          setMensajeroFilter({ id: mensajero.id, nombre: mensajero.nombre, tipo: "completado" });
                          setEstadoFilter("completado");
                          setUrgenteFilter("all");
                          setSearchQuery("");
                        }
                      }}
                      data-testid={`mensajero-${mensajero.id}-completado`}
                    >
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">{mensajero.completadas_mes || 0}</p>
                      <p className="text-xs text-green-600/70 dark:text-green-400/70">Entregas (Mes)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card className="border-slate-200 dark:border-slate-700 mb-6 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por cliente, documento, dirección..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
              </div>
              
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger className="w-[150px] dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="en_proceso">En Proceso</SelectItem>
                  <SelectItem value="completado">Completado</SelectItem>
                  <SelectItem value="devuelto">Devuelto</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={urgenteFilter} onValueChange={setUrgenteFilter}>
                <SelectTrigger className="w-[150px] dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Urgencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="urgente">Urgentes</SelectItem>
                  <SelectItem value="normal">Normales</SelectItem>
                </SelectContent>
              </Select>
              
              <ViewSelector currentView={viewMode} onViewChange={setViewMode} />
            </div>
            {mensajeroFilter && (
              <div className="mt-3 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 gap-1.5 py-1 px-3">
                  <User className="w-3 h-3" />
                  {mensajeroFilter.nombre}: {mensajeroFilter.tipo === "en_proceso" ? "En Proceso" : "Completados (Mes)"}
                  <button 
                    onClick={() => setMensajeroFilter(null)}
                    className="ml-1 hover:text-purple-900 dark:hover:text-purple-100"
                    data-testid="clear-mensajero-filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
        {/* FIN STICKY HEADER */}

        {/* Solicitudes Content - Multiple Views */}
        <div className="pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredSolicitudes.length === 0 ? (
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">No hay solicitudes</p>
            </CardContent>
          </Card>
        ) : viewMode === 'kanban' ? (
          <KanbanView 
            items={pagedFiltered}
            columns={[
              { id: 'pendiente', label: 'Pendiente', color: 'bg-yellow-500' },
              { id: 'en_proceso', label: 'En Proceso', color: 'bg-blue-500' },
              { id: 'completado', label: 'Completado', color: 'bg-green-500' },
              { id: 'devuelto', label: 'Devuelto', color: 'bg-orange-500' },
            ]}
            statusField="estado"
            renderCard={(sol) => (
              <div>
                <div className="flex items-center justify-between mb-1">
                  {sol.es_urgente && (
                    <Badge className="bg-red-100 text-red-700 text-xs">URGENTE</Badge>
                  )}
                  <Badge variant="outline" className="text-xs ml-auto">
                    {sol.tipo_documento_nombre || sol.tipo}
                  </Badge>
                </div>
                <p className="font-medium text-sm text-slate-900 dark:text-white">{sol.cliente}</p>
                {sol.envio_sucursal && sol.sucursal && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700 mt-0.5">
                    <Building2 className="w-3 h-3 mr-1" />{sol.sucursal}
                  </Badge>
                )}
                {/* Descripción del documento */}
                {(sol.documento_descripcion || sol.descripcion) && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 line-clamp-2" data-testid={`kanban-doc-desc-${sol.id}`}>
                    {sol.documento_descripcion || sol.descripcion}
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {sol.destino_direccion || sol.direccion_envio || 'Sin dirección'}
                </p>
                {/* Time Indicators */}
                <TimeIndicators solicitud={sol} />
              </div>
            )}
            onItemClick={(sol) => openViewDialog(sol)}
          />
        ) : viewMode === 'cards' ? (
          <CardsView
            items={pagedFiltered}
            columns={3}
            renderCard={(sol) => (
              <>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    {sol.es_urgente && (
                      <Badge className="bg-red-100 text-red-700 text-xs">URGENTE</Badge>
                    )}
                    <Badge className={`text-xs ml-auto ${
                      sol.estado === 'completado' ? 'bg-green-100 text-green-700' :
                      sol.estado === 'en_proceso' ? 'bg-blue-100 text-blue-700' :
                      sol.estado === 'devuelto' ? 'bg-orange-100 text-orange-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {sol.estado}
                    </Badge>
                  </div>
                  <CardTitle className="text-base mt-2">{sol.cliente}</CardTitle>
                  {sol.envio_sucursal && sol.sucursal && (
                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700 mt-1">
                      <Building2 className="w-3 h-3 mr-1" />{sol.sucursal}
                    </Badge>
                  )}
                  {/* Descripción del documento */}
                  {(sol.documento_descripcion || sol.descripcion) && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1 line-clamp-2" data-testid={`card-doc-desc-${sol.id}`}>
                      {sol.documento_descripcion || sol.descripcion}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="pb-2">
                  <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{sol.destino_direccion || sol.direccion_envio || 'Sin dirección'}</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">{sol.descripcion || sol.notas}</p>
                  {/* Time Indicators */}
                  <TimeIndicators solicitud={sol} />
                </CardContent>
                <div className="px-6 pb-4 flex justify-between items-center">
                  <Badge variant="outline">{sol.tipo_documento_nombre || sol.tipo}</Badge>
                  <span className="text-xs text-slate-400">
                    {new Date(sol.created_at).toLocaleDateString('es-DO')}
                  </span>
                </div>
              </>
            )}
            onItemClick={(sol) => openViewDialog(sol)}
          />
        ) : viewMode === 'list' ? (
          <ListView
            items={pagedFiltered}
            renderItem={(sol) => (
              <>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {sol.es_urgente && (
                      <Badge className="bg-red-100 text-red-700 text-xs">URGENTE</Badge>
                    )}
                    <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{sol.cliente}</p>
                    {sol.envio_sucursal && sol.sucursal && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                        <Building2 className="w-3 h-3 mr-1" />{sol.sucursal}
                      </Badge>
                    )}
                  </div>
                  {/* Descripción del documento */}
                  {(sol.documento_descripcion || sol.descripcion) && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 truncate mt-0.5" data-testid={`list-doc-desc-${sol.id}`}>
                      {sol.documento_descripcion || sol.descripcion}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {sol.destino_direccion || sol.direccion_envio || 'Sin dirección'}
                  </p>
                  {/* Time Indicators */}
                  <TimeIndicators solicitud={sol} />
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <Badge variant="outline" className="text-xs">{sol.tipo_documento_nombre || sol.tipo}</Badge>
                  <Badge className={`text-xs ${
                    sol.estado === 'completado' ? 'bg-green-100 text-green-700' :
                    sol.estado === 'en_proceso' ? 'bg-blue-100 text-blue-700' :
                    sol.estado === 'devuelto' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {sol.estado}
                  </Badge>
                </div>
              </>
            )}
            onItemClick={(sol) => openViewDialog(sol)}
          />
        ) : (
          /* Table View - Default */
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                  Solicitudes ({filteredSolicitudes.length})
                </CardTitle>
                <div className="flex items-center gap-3">
                  {/* Select All Checkbox - Hidden for Recepcionista */}
                  {canEdit() && (
                    <>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="select-all"
                          checked={selectedItems.length === filteredSolicitudes.length && filteredSolicitudes.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                        <Label htmlFor="select-all" className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                          {selectedItems.length > 0 ? `${selectedItems.length} seleccionadas` : "Seleccionar todo"}
                        </Label>
                      </div>
                      
                      {/* Bulk Delete Button */}
                      {selectedItems.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={deleteBulkSolicitudes}
                          disabled={deletingBulk}
                          data-testid="bulk-delete-btn"
                        >
                          {deletingBulk ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Eliminar ({selectedItems.length})
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {/* Sucursal Groups */}
                {sucursalGroups.map((grupo) => (
                  <div key={`grupo-${grupo.sucursal}`} data-testid={`grupo-sucursal-${grupo.sucursal}`}>
                    {/* Group Header Row */}
                    <div
                      className="flex items-center justify-between p-4 bg-purple-50/60 dark:bg-purple-900/20 cursor-pointer hover:bg-purple-100/60 dark:hover:bg-purple-900/30 transition-colors"
                      onClick={() => toggleSucursalExpanded(grupo.sucursal)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-purple-100 dark:bg-purple-900/40">
                          <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {expandedSucursales[grupo.sucursal] ? (
                              <ChevronDown className="w-4 h-4 text-purple-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-purple-600" />
                            )}
                            <p className="font-semibold text-purple-900 dark:text-purple-200">{grupo.sucursal}</p>
                            <Badge className="bg-purple-200 text-purple-800 dark:bg-purple-800 dark:text-purple-200 border-0">
                              {grupo.count} {grupo.count === 1 ? 'envío' : 'envíos'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {grupo.pendientes > 0 && (
                              <span className="text-xs text-yellow-600 dark:text-yellow-400">{grupo.pendientes} pendiente{grupo.pendientes > 1 ? 's' : ''}</span>
                            )}
                            {grupo.enProceso > 0 && (
                              <span className="text-xs text-blue-600 dark:text-blue-400">{grupo.enProceso} en proceso</span>
                            )}
                            {grupo.devueltos > 0 && (
                              <span className="text-xs text-orange-600 dark:text-orange-400">{grupo.devueltos} devuelto{grupo.devueltos > 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {grupo.dominantEstado === "pendiente" || grupo.pendientes > 0 ? (
                          <>
                            {canChangeStatus() && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateGrupoEstado(grupo.sucursal, "en_proceso")}
                                disabled={processingGrupo === grupo.sucursal}
                                className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800"
                                data-testid={`grupo-iniciar-${grupo.sucursal}`}
                              >
                                {processingGrupo === grupo.sucursal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                                Iniciar Grupo
                              </Button>
                            )}
                          </>
                        ) : null}
                        {(grupo.dominantEstado === "en_proceso" || grupo.enProceso > 0) && canChangeStatus() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGrupoCompletarClick(grupo)}
                            disabled={processingGrupo === grupo.sucursal}
                            className="border-green-200 text-green-600 hover:bg-green-50 dark:border-green-800"
                            data-testid={`grupo-completar-${grupo.sucursal}`}
                          >
                            {processingGrupo === grupo.sucursal ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1" />}
                            Completar Grupo
                          </Button>
                        )}
                        {getEstadoBadge(grupo.dominantEstado === "mixto" ? "en_proceso" : grupo.dominantEstado)}
                      </div>
                    </div>
                    {/* Expanded: Show individual solicitudes in this group */}
                    {expandedSucursales[grupo.sucursal] && (
                      <div className="border-l-4 border-purple-300 dark:border-purple-700">
                        {grupo.items.map((sol) => (
                          <div
                            key={sol.id}
                            className={`flex items-center justify-between p-4 pl-8 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors border-b border-slate-50 dark:border-slate-700/50 ${selectedItems.includes(sol.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          >
                            <div className="flex items-center gap-4">
                              {canEdit() && (
                                <Checkbox
                                  checked={selectedItems.includes(sol.id)}
                                  onCheckedChange={() => toggleSelectItem(sol.id)}
                                  data-testid={`select-item-${sol.id}`}
                                />
                              )}
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sol.es_urgente ? 'bg-red-100 dark:bg-red-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                                <Send className={`w-5 h-5 ${sol.es_urgente ? 'text-red-600' : 'text-slate-600 dark:text-slate-400'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-slate-900 dark:text-white text-sm">{sol.cliente}</p>
                                  {sol.es_urgente && <Badge className="bg-red-500 text-white border-0 text-xs">URGENTE</Badge>}
                                  {sol.mensajero_name && (
                                    <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
                                      <User className="w-3 h-3 mr-1" />{sol.mensajero_name}
                                    </Badge>
                                  )}
                                </div>
                                {(sol.documento_descripcion || sol.descripcion || sol.tipo_documento_nombre) && (
                                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                                    <FileText className="w-3 h-3 text-blue-500 inline mr-1" />
                                    {sol.documento_descripcion || sol.descripcion || sol.tipo_documento_nombre}
                                  </p>
                                )}
                                <span className="text-xs text-slate-400">{new Date(sol.fecha_creacion).toLocaleDateString('es-ES')}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getEstadoBadge(sol.estado)}
                              <Button variant="outline" size="sm" onClick={() => openViewDialog(sol)} className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700" data-testid={`view-solicitud-${sol.id}`}>
                                <Eye className="w-4 h-4 mr-1" />Ver
                              </Button>
                              {canEdit(sol) && (
                                <Button variant="outline" size="sm" onClick={() => openEditDialog(sol)} className="dark:border-slate-600" data-testid={`edit-solicitud-${sol.id}`}><Edit2 className="w-4 h-4" /></Button>
                              )}
                              {canEdit() && (
                                <Button variant="outline" size="sm" onClick={() => deleteSolicitud(sol.id)} className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800" data-testid={`delete-solicitud-${sol.id}`}><Trash2 className="w-4 h-4" /></Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {/* Individual Solicitudes (not grouped) */}
                {individualSolicitudes.length > 0 && pagedIndividual.map((sol) => (
                  <div
                    key={sol.id}
                    className={`flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${selectedItems.includes(sol.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Item Checkbox - Hidden for Recepcionista */}
                      {canEdit() && (
                        <Checkbox
                          checked={selectedItems.includes(sol.id)}
                          onCheckedChange={() => toggleSelectItem(sol.id)}
                          data-testid={`select-item-${sol.id}`}
                        />
                      )}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${sol.es_urgente ? 'bg-red-100 dark:bg-red-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                        {sol.tipo === "entrega" ? (
                          <Send className={`w-6 h-6 ${sol.es_urgente ? 'text-red-600' : 'text-slate-600 dark:text-slate-400'}`} />
                        ) : (
                          <Package className={`w-6 h-6 ${sol.es_urgente ? 'text-red-600' : 'text-slate-600 dark:text-slate-400'}`} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-slate-900 dark:text-white">{sol.cliente}</p>
                          {sol.mensajero_name && (
                            <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700" data-testid={`mensajero-badge-${sol.id}`}>
                              <User className="w-3 h-3 mr-1" />
                              {sol.mensajero_name}
                            </Badge>
                          )}
                          {sol.user_id !== user?.id && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                              <Users className="w-3 h-3 mr-1" />
                              Supervisado{sol.created_by_name ? `: ${sol.created_by_name}` : ''}
                            </Badge>
                          )}
                          {sol.es_urgente && (
                            <Badge className="bg-red-500 text-white border-0 text-xs">URGENTE</Badge>
                          )}
                          {sol.envio_sucursal && sol.sucursal && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700" data-testid={`sucursal-badge-${sol.id}`}>
                              <Building2 className="w-3 h-3 mr-1" />
                              {sol.sucursal}
                            </Badge>
                          )}
                        </div>
                        {/* Descripción del documento - visible para mensajeros */}
                        {(sol.documento_descripcion || sol.descripcion || sol.tipo_documento_nombre) && (
                          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 flex items-start gap-1" data-testid={`doc-desc-${sol.id}`}>
                            <FileText className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span className="font-medium">{sol.documento_descripcion || sol.descripcion || sol.tipo_documento_nombre}</span>
                          </p>
                        )}
                        <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {sol.direccion_envio?.slice(0, 40)}...
                          </span>
                          {sol.archivo_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadArchivoAdjunto(sol)}
                              className="h-6 px-2 text-blue-600 dark:text-blue-400"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Archivo
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">
                            {new Date(sol.fecha_creacion).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                        {/* Time Indicators */}
                        <TimeIndicators solicitud={sol} />
                        {/* GPS Button for navigation */}
                        {sol.direccion_envio && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const address = encodeURIComponent(sol.direccion_envio);
                              window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=driving`, '_blank');
                            }}
                            className="mt-2 w-full bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400"
                            data-testid={`gps-btn-${sol.id}`}
                          >
                            <Navigation className="w-4 h-4 mr-2" />
                            Abrir GPS
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {getEstadoBadge(sol.estado)}
                      
                      {canChangeStatus() && sol.estado === "pendiente" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateEstado(sol.id, "en_proceso")}
                          className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800"
                        >
                          Iniciar
                        </Button>
                      )}
                      
                      {canChangeStatus() && sol.estado === "en_proceso" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCompletadoClick(sol)}
                          className="border-green-200 text-green-600 hover:bg-green-50 dark:border-green-800"
                        >
                          <PackageCheck className="w-4 h-4 mr-1" />
                          Completar
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openViewDialog(sol)}
                        className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
                        data-testid={`view-solicitud-${sol.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      
                      {canEdit(sol) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(sol)}
                          className="dark:border-slate-600"
                          data-testid={`edit-solicitud-${sol.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {canEdit() && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteSolicitud(sol.id)}
                          className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Paginación (client-side) — mantiene la página rápida con históricos grandes */}
        {!loading && filteredSolicitudes.length > 0 && pageBaseCount > pageSize && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4" data-testid="mensajeria-pagination">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Mostrando {pageStart + 1}–{Math.min(pageEnd, pageBaseCount)} de {pageBaseCount}
              {viewMode === 'table' && sucursalGroups.length > 0 ? ' individuales' : ''}
            </p>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="w-[120px] dark:bg-slate-700 dark:border-slate-600" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / página</SelectItem>
                  <SelectItem value="50">50 / página</SelectItem>
                  <SelectItem value="100">100 / página</SelectItem>
                  <SelectItem value="200">200 / página</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, Math.min(p, totalPages) - 1))}
                data-testid="page-prev-btn"
                className="dark:border-slate-600"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-300 min-w-[96px] text-center">
                Página {safePage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, Math.min(p, totalPages) + 1))}
                data-testid="page-next-btn"
                className="dark:border-slate-600"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        </div>
        {/* FIN CONTENIDO */}

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent
            className="max-w-lg dark:bg-slate-800"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="dark:text-white">
                {editingSolicitud ? "Editar Solicitud" : "Nueva Solicitud"}
              </DialogTitle>
              <DialogDescription className="dark:text-slate-400">
                Complete los datos de la solicitud de mensajería
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Cliente Selector with Combobox */}
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Cliente *</Label>
                <div className="flex gap-2">
                  <Popover open={clienteSearchOpen} onOpenChange={setClienteSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={clienteSearchOpen}
                        className="flex-1 justify-between dark:bg-slate-700 dark:border-slate-600"
                        data-testid="cliente-combobox"
                      >
                        {formData.cliente || "Seleccionar cliente..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0">
                      <Command>
                        <CommandInput 
                          placeholder="Buscar cliente..." 
                          value={clienteSearchQuery}
                          onValueChange={setClienteSearchQuery}
                        />
                        <CommandList>
                          <CommandEmpty>No se encontraron clientes</CommandEmpty>
                          <CommandGroup>
                            {clientes
                              .filter(c => 
                                c.nombre.toLowerCase().includes(clienteSearchQuery.toLowerCase()) ||
                                (c.direccion || "").toLowerCase().includes(clienteSearchQuery.toLowerCase())
                              )
                              .slice(0, 10)
                              .map((cliente) => (
                                <CommandItem
                                  key={cliente.id}
                                  value={cliente.nombre}
                                  onSelect={() => handleClienteSelect(cliente)}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      formData.cliente_id === cliente.id ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                  <div className="flex flex-col">
                                    <span>{cliente.nombre}</span>
                                    {cliente.direccion && (
                                      <span className="text-xs text-slate-500">{cliente.direccion}</span>
                                    )}
                                  </div>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowNewClienteDialog(true)}
                    title="Agregar nuevo cliente"
                    data-testid="add-new-cliente-btn"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>
                {/* Allow manual input if needed */}
                <Input
                  value={formData.cliente}
                  onChange={(e) => setFormData({...formData, cliente: e.target.value, cliente_id: ""})}
                  placeholder="O escriba el nombre manualmente"
                  className="dark:bg-slate-700 dark:border-slate-600 text-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Dirección de Envío *</Label>
                <Textarea
                  value={formData.direccion_envio}
                  onChange={(e) => setFormData({...formData, direccion_envio: e.target.value})}
                  placeholder="Dirección completa"
                  className="dark:bg-slate-700 dark:border-slate-600"
                  rows={2}
                />
                {formData.direccion_envio && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const address = encodeURIComponent(formData.direccion_envio);
                      // Try to open in Google Maps app first, fallback to browser
                      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                      if (isMobile) {
                        // Mobile: Try to open native maps app
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=driving`, '_blank');
                      } else {
                        // Desktop: Open Google Maps in browser
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=driving`, '_blank');
                      }
                    }}
                    className="w-full mt-2 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/40"
                    data-testid="gps-button"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    GPS - Abrir Navegación
                  </Button>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    Teléfono
                  </Label>
                  <Input
                    value={formData.telefono_cliente}
                    onChange={(e) => setFormData({...formData, telefono_cliente: e.target.value})}
                    placeholder="809-000-0000"
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-1">
                    <Smartphone className="w-3 h-3" />
                    Celular
                  </Label>
                  <Input
                    value={formData.celular_cliente}
                    onChange={(e) => setFormData({...formData, celular_cliente: e.target.value})}
                    placeholder="809-000-0000"
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
              </div>

              {/* Envío a Sucursal */}
              <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between">
                  <Label className="dark:text-slate-300 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    Envío a Sucursal
                  </Label>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${!formData.envio_sucursal ? 'font-medium text-slate-700' : 'text-slate-400'}`}>No</span>
                    <Switch
                      checked={formData.envio_sucursal}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          envio_sucursal: checked,
                          sucursal: checked ? prev.sucursal : ""
                        }));
                      }}
                      data-testid="envio-sucursal-switch"
                    />
                    <span className={`text-sm ${formData.envio_sucursal ? 'font-medium text-purple-700' : 'text-slate-400'}`}>Sí</span>
                  </div>
                </div>
                
                {formData.envio_sucursal && (
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300 text-sm">Seleccionar Sucursal *</Label>
                    <Select
                      value={formData.sucursal}
                      onValueChange={(value) => setFormData({...formData, sucursal: value})}
                    >
                      <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="select-sucursal">
                        <SelectValue placeholder="Seleccionar sucursal" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUCURSALES_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Tipo de Documento</Label>
                <Select 
                  value={formData.tipo_documento_id || "none"} 
                  onValueChange={(v) => handleTipoDocumentoChange(v === "none" ? "" : v)}
                >
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    {tiposDocumento.map(tipo => (
                      <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {loadingPlantilla && (
                  <p className="text-xs text-blue-500">Cargando plantilla...</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="dark:text-slate-300">Descripción del Documento</Label>
                <Textarea
                  value={formData.documento_descripcion}
                  onChange={(e) => setFormData({...formData, documento_descripcion: e.target.value})}
                  placeholder="Descripción adicional del documento"
                  className="dark:bg-slate-700 dark:border-slate-600"
                  rows={3}
                />
              </div>

              {/* Pólizas de Renovación (RENOVADA) del cliente */}
              <div className="space-y-2 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800" data-testid="polizas-renovacion-section">
                <Label className="dark:text-slate-300 flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-semibold">
                  <FileText className="w-4 h-4" />
                  Pólizas de Renovación (estatus Renovada)
                </Label>
                <p className="text-xs text-emerald-700 dark:text-emerald-400">
                  Seleccione la(s) póliza(s) que está enviando. Esto permite calcular con exactitud la columna "Dif. recibido / vigencia" en Estadísticas.
                </p>
                {loadingPolizas ? (
                  <p className="text-xs text-slate-500">Buscando pólizas renovadas del cliente...</p>
                ) : polizasDisponibles.length === 0 ? (
                  <p className="text-xs text-slate-500" data-testid="polizas-renovacion-empty">
                    {formData.cliente ? "No hay pólizas renovadas para este cliente." : "Seleccione un cliente para ver sus pólizas renovadas."}
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {polizasDisponibles.map((p) => {
                      const checked = (formData.polizas_renovacion || []).some(x => x.renovacion_id === p.renovacion_id);
                      return (
                        <label
                          key={p.renovacion_id}
                          className="flex items-start gap-2 p-2 rounded-md bg-white dark:bg-slate-800 border border-emerald-100 dark:border-emerald-800/50 cursor-pointer hover:bg-emerald-100/40"
                          data-testid={`poliza-renovacion-${p.poliza}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => togglePolizaRenovacion(p)}
                            className="mt-0.5"
                            data-testid={`poliza-renovacion-check-${p.poliza}`}
                          />
                          <div className="flex flex-col leading-tight">
                            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Póliza: {p.poliza || "—"}</span>
                            <span className="text-[11px] text-slate-500 dark:text-slate-400">
                              {p.ramo || ""}{p.compania ? ` · ${p.compania}` : ""}{p.vence_en ? ` · Vence: ${p.vence_en}` : ""}
                              {p.entregado_cliente ? " · Ya entregada" : ""}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Custom Template Fields */}
              {templateFields.length > 0 && (
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FormInput className="w-5 h-5 text-[#0C314C]" />
                    <h4 className="font-medium text-slate-900 dark:text-white">
                      Campos Personalizados de la Plantilla
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-testid="custom-fields-section">
                    {templateFields
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map(field => renderCustomField(field))}
                  </div>
                </div>
              )}

              {/* File Upload Section */}
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Adjuntar Documento (PDF, JPEG, PNG)</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="dark:bg-slate-700 dark:border-slate-600 flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {selectedFile ? selectedFile.name : (editingSolicitud?.archivo_nombre || "Seleccionar archivo")}
                  </Button>
                  {(selectedFile || editingSolicitud?.archivo_url) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedFile(null)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {editingSolicitud?.archivo_url && !selectedFile && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    Archivo actual: {editingSolicitud.archivo_nombre}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Tipo</Label>
                  <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                    <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entrega">Entrega</SelectItem>
                      <SelectItem value="recogida">Recogida</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Mensajero</Label>
                  <Select value={formData.mensajero_id || "none"} onValueChange={(v) => setFormData({...formData, mensajero_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {mensajeros.filter(m => m.is_active).map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <Label className="text-red-700 dark:text-red-300">Marcar como URGENTE</Label>
                </div>
                <Switch
                  checked={formData.es_urgente}
                  onCheckedChange={(v) => setFormData({...formData, es_urgente: v})}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Notas</Label>
                <Textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({...formData, notas: e.target.value})}
                  placeholder="Notas adicionales"
                  className="dark:bg-slate-700 dark:border-slate-600"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              {!editingSolicitud && (
                <Button
                  variant="ghost"
                  onClick={discardDraft}
                  className="text-rose-600 hover:bg-rose-50 mr-auto"
                  data-testid="descartar-borrador-btn"
                >
                  Descartar borrador
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowDialog(false)} className="dark:border-slate-600">
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                disabled={uploadingFile}
              >
                {uploadingFile ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  editingSolicitud ? "Actualizar" : "Crear"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo de Confirmación de Entrega */}
        <Dialog open={showEntregaDialog} onOpenChange={setShowEntregaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PackageCheck className="w-5 h-5 text-green-600" />
                Confirmar Entrega
              </DialogTitle>
              <DialogDescription>
                Complete los datos de entrega para marcar esta solicitud como completada.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_entrega" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Fecha de Entrega *
                </Label>
                <Input
                  id="fecha_entrega"
                  type="date"
                  value={entregaData.fecha_entrega}
                  onChange={(e) => setEntregaData({...entregaData, fecha_entrega: e.target.value})}
                  className="border-slate-200 dark:bg-slate-700 dark:border-slate-600"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="soporte_entrega" className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Soporte de Entrega (Foto/PDF) {appSettings.photo_required && <span className="text-red-500">*</span>}
                </Label>
                {appSettings.photo_required && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    La foto de soporte es obligatoria para confirmar la entrega
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    ref={soporteInputRef}
                    id="soporte_entrega"
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={(e) => setEntregaData({...entregaData, soporte_file: e.target.files[0]})}
                    className={`border-slate-200 dark:bg-slate-700 dark:border-slate-600 ${appSettings.photo_required && !entregaData.soporte_file ? 'border-red-300 dark:border-red-600' : ''}`}
                  />
                </div>
                {entregaData.soporte_file && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 p-2 rounded">
                    <FileText className="w-4 h-4" />
                    <span>{entregaData.soporte_file.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEntregaData({...entregaData, soporte_file: null});
                        if (soporteInputRef.current) soporteInputRef.current.value = "";
                      }}
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Formatos permitidos: JPG, PNG, PDF. Máximo 10MB.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEntregaDialog(false);
                  setSelectedSolicitudForEntrega(null);
                  setEntregaData({ fecha_entrega: "", soporte_file: null });
                }}
                disabled={uploadingSoporte}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmEntrega}
                disabled={!entregaData.fecha_entrega || (appSettings.photo_required && !entregaData.soporte_file) || uploadingSoporte}
                className="bg-green-600 hover:bg-green-700"
              >
                {uploadingSoporte ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <PackageCheck className="w-4 h-4 mr-2" />
                    Confirmar Entrega
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo de Confirmación de Entrega de Grupo de Sucursal */}
        <Dialog open={showGrupoEntregaDialog} onOpenChange={setShowGrupoEntregaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-600" />
                Completar Grupo - {selectedGrupoForEntrega?.sucursal}
              </DialogTitle>
              <DialogDescription>
                Se completarán {selectedGrupoForEntrega?.count || 0} envíos de esta sucursal.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* List of items being completed */}
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-2">Envíos incluidos:</p>
                {selectedGrupoForEntrega?.items?.map((sol, idx) => (
                  <p key={sol.id} className="text-xs text-purple-600 dark:text-purple-400">
                    {idx + 1}. {sol.cliente} - {sol.documento_descripcion || sol.tipo_documento_nombre || sol.tipo}
                  </p>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="grupo_fecha_entrega" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Fecha de Entrega *
                </Label>
                <Input
                  id="grupo_fecha_entrega"
                  type="date"
                  value={grupoEntregaData.fecha_entrega}
                  onChange={(e) => setGrupoEntregaData({...grupoEntregaData, fecha_entrega: e.target.value})}
                  className="border-slate-200 dark:bg-slate-700 dark:border-slate-600"
                  required
                  data-testid="grupo-fecha-entrega"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="grupo_soporte" className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Foto de Acuse (opcional)
                </Label>
                <Input
                  ref={grupoSoporteInputRef}
                  id="grupo_soporte"
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={(e) => setGrupoEntregaData({...grupoEntregaData, soporte_file: e.target.files[0]})}
                  className="border-slate-200 dark:bg-slate-700 dark:border-slate-600"
                  data-testid="grupo-soporte-input"
                />
                {grupoEntregaData.soporte_file && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 p-2 rounded">
                    <FileText className="w-4 h-4" />
                    <span>{grupoEntregaData.soporte_file.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setGrupoEntregaData({...grupoEntregaData, soporte_file: null});
                        if (grupoSoporteInputRef.current) grupoSoporteInputRef.current.value = "";
                      }}
                      className="h-6 w-6 p-0 text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setShowGrupoEntregaDialog(false); setSelectedGrupoForEntrega(null); }}
                disabled={uploadingGrupoSoporte}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmGrupoEntrega}
                disabled={!grupoEntregaData.fecha_entrega || uploadingGrupoSoporte}
                className="bg-green-600 hover:bg-green-700"
                data-testid="grupo-confirmar-entrega"
              >
                {uploadingGrupoSoporte ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                ) : (
                  <><PackageCheck className="w-4 h-4 mr-2" />Completar {selectedGrupoForEntrega?.count || 0} envíos</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de Vista de Detalles */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedSolicitud && (
              <>
                <ClienteAtencionWatcher nombre={selectedSolicitud.cliente} />
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#0C314C]" />
                    Solicitud de Mensajería
                    {selectedSolicitud.es_urgente && (
                      <Badge className="bg-red-500 text-white border-0 ml-2">URGENTE</Badge>
                    )}
                  </DialogTitle>
                  <div className="flex items-center gap-2 mt-2">
                    {getEstadoBadge(selectedSolicitud.estado)}
                    <Badge variant="outline" className="capitalize">
                      {selectedSolicitud.tipo === "entrega" ? "Entrega" : "Recogida"}
                    </Badge>
                    {selectedSolicitud.envio_sucursal && selectedSolicitud.sucursal && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                        <Building2 className="w-3 h-3 mr-1" />
                        {selectedSolicitud.sucursal}
                      </Badge>
                    )}
                  </div>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Cliente y Dirección */}
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Cliente</Label>
                        <p className="text-slate-900 dark:text-white font-medium">{selectedSolicitud.cliente}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Destinatario</Label>
                        <p className="text-slate-900 dark:text-white font-medium">
                          {selectedSolicitud.destino_nombre || selectedSolicitud.cliente || "No especificado"}
                        </p>
                      </div>
                    </div>
                    
                    {/* Descripción del documento - Campo prominente para el mensajero */}
                    {(selectedSolicitud.descripcion || selectedSolicitud.documento_descripcion) && (
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg" data-testid="view-dialog-descripcion">
                        <Label className="text-xs text-blue-600 dark:text-blue-400 font-semibold flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          Descripción del Documento
                        </Label>
                        <p className="text-slate-900 dark:text-white font-medium mt-1">
                          {selectedSolicitud.descripcion || selectedSolicitud.documento_descripcion}
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Tipo de Documento</Label>
                        <p className="text-slate-900 dark:text-white font-medium">
                          {selectedSolicitud.tipo_documento_nombre || "No especificado"}
                        </p>
                      </div>
                      {selectedSolicitud.destino_telefono && (
                        <div>
                          <Label className="text-xs text-slate-500 dark:text-slate-400">Teléfono Destinatario</Label>
                          <p className="text-slate-900 dark:text-white font-medium">
                            <a href={`tel:${selectedSolicitud.destino_telefono}`} className="text-blue-600 hover:underline">
                              {selectedSolicitud.destino_telefono}
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="mt-4">
                      <Label className="text-xs text-slate-500 dark:text-slate-400">Dirección de Envío</Label>
                      <p className="text-slate-900 dark:text-white font-medium flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-slate-400" />
                        {selectedSolicitud.destino_direccion || selectedSolicitud.direccion_envio || "No especificada"}
                      </p>
                      {(selectedSolicitud.destino_direccion || selectedSolicitud.direccion_envio) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const address = encodeURIComponent(selectedSolicitud.destino_direccion || selectedSolicitud.direccion_envio);
                            window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=driving`, '_blank');
                          }}
                          className="mt-2 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                        >
                          <Navigation className="w-4 h-4 mr-2" />
                          Abrir GPS
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Información adicional */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400" data-testid="detail-mensajero">
                      <User className="w-4 h-4" />
                      <span>Mensajero: {selectedSolicitud.mensajero_name 
                        ? <span className="font-medium text-purple-600">{selectedSolicitud.mensajero_name}</span>
                        : <span className="font-medium text-orange-500">Sin asignar</span>
                      }</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Clock className="w-4 h-4" />
                      <span>Creado: {new Date(selectedSolicitud.fecha_creacion).toLocaleDateString('es-ES')}</span>
                    </div>
                    {selectedSolicitud.fecha_entrega && (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Entregado: {new Date(selectedSolicitud.fecha_entrega).toLocaleDateString('es-ES')}</span>
                      </div>
                    )}
                  </div>

                  {/* Notas */}
                  {selectedSolicitud.notas && (
                    <div>
                      <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Notas</Label>
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm text-slate-700 dark:text-slate-300">
                        {selectedSolicitud.notas}
                      </div>
                    </div>
                  )}

                  {/* Custom Fields Data Display */}
                  {selectedSolicitud.custom_fields_data && Object.keys(selectedSolicitud.custom_fields_data).length > 0 && (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FormInput className="w-4 h-4 text-[#0C314C]" />
                        <Label className="text-slate-900 dark:text-white font-medium">Campos Personalizados</Label>
                      </div>
                      <div className="grid grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                        {(selectedSolicitud.custom_fields_schema || []).map((field) => {
                          const fieldKey = field.name || field.id;
                          const value = selectedSolicitud.custom_fields_data[fieldKey];
                          if (value === undefined || value === "" || value === null) return null;
                          
                          const displayValue = field.field_type === "checkbox" 
                            ? (value === true || value === "true" ? "Sí" : "No")
                            : field.field_type === "date" && value
                              ? new Date(value).toLocaleDateString('es-ES')
                              : value;
                          
                          return (
                            <div key={fieldKey} className="text-sm">
                              <span className="text-slate-500 dark:text-slate-400">{field.label}: </span>
                              <span className="text-slate-900 dark:text-white font-medium">{displayValue}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Archivo adjunto */}
                  {selectedSolicitud.archivo_url && (
                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-blue-500" />
                        <span className="text-sm text-blue-700 dark:text-blue-300">
                          Archivo adjunto: {selectedSolicitud.archivo_nombre || 'documento'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewArchivoAdjunto(selectedSolicitud)}
                          className="border-blue-300 text-blue-600 hover:bg-blue-100"
                          data-testid="view-archivo-btn"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadArchivoAdjunto(selectedSolicitud)}
                          data-testid="download-archivo-btn"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Descargar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Soporte de entrega */}
                  {selectedSolicitud.soporte_entrega_url && (
                    <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                        <span className="text-sm text-green-700 dark:text-green-300">
                          Soporte de entrega
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}${selectedSolicitud.soporte_entrega_url}`, '_blank')}
                        className="border-green-300 text-green-600 hover:bg-green-100"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Ver soporte
                      </Button>
                    </div>
                  )}

                  {/* Confirmación de entrega (firma y foto del mensajero) */}
                  {selectedSolicitud.confirmacion_entrega && (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <Label className="text-slate-900 dark:text-white font-medium">Confirmación de Entrega</Label>
                      </div>
                      
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 space-y-4">
                        {/* Info de confirmación */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">Recibido por: </span>
                            <span className="font-medium text-slate-900 dark:text-white">{selectedSolicitud.confirmacion_entrega.recipient_name}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">Mensajero: </span>
                            <span className="font-medium text-slate-900 dark:text-white">{selectedSolicitud.confirmacion_entrega.mensajero_name}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">Fecha: </span>
                            <span className="font-medium text-slate-900 dark:text-white">
                              {new Date(selectedSolicitud.confirmacion_entrega.confirmed_at).toLocaleString('es-DO')}
                            </span>
                          </div>
                          {selectedSolicitud.confirmacion_entrega.notes && (
                            <div className="col-span-2">
                              <span className="text-slate-500 dark:text-slate-400">Notas: </span>
                              <span className="text-slate-900 dark:text-white">{selectedSolicitud.confirmacion_entrega.notes}</span>
                            </div>
                          )}
                        </div>

                        {/* Firma y Foto */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-green-200 dark:border-green-800">
                          {/* Firma */}
                          {selectedSolicitud.confirmacion_entrega.signature_url && (
                            <div>
                              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">Firma del Receptor</Label>
                              <div className="bg-white dark:bg-slate-800 rounded-lg p-2 border border-slate-200 dark:border-slate-700">
                                <img 
                                  src={`${API}${selectedSolicitud.confirmacion_entrega.signature_url}`}
                                  alt="Firma del receptor"
                                  className="max-h-32 mx-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => window.open(`${API}${selectedSolicitud.confirmacion_entrega.signature_url}`, '_blank')}
                                />
                              </div>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => window.open(`${API}${selectedSolicitud.confirmacion_entrega.signature_url}`, '_blank')}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Ver firma
                                </Button>
                                {isAdmin() && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-300"
                                    onClick={() => {
                                      const signatureId = selectedSolicitud.confirmacion_entrega.signature_url?.split('/').pop();
                                      if (signatureId) deleteDeliveryImage(signatureId, 'signature');
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Foto */}
                          {selectedSolicitud.confirmacion_entrega.photo_url && (
                            <div>
                              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">Foto de Entrega</Label>
                              <div className="bg-white dark:bg-slate-800 rounded-lg p-2 border border-slate-200 dark:border-slate-700">
                                <img 
                                  src={`${API}${selectedSolicitud.confirmacion_entrega.photo_url}`}
                                  alt="Foto de entrega"
                                  className="max-h-32 mx-auto object-contain cursor-pointer hover:opacity-80 transition-opacity rounded"
                                  onClick={() => window.open(`${API}${selectedSolicitud.confirmacion_entrega.photo_url}`, '_blank')}
                                />
                              </div>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => window.open(`${API}${selectedSolicitud.confirmacion_entrega.photo_url}`, '_blank')}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Ver foto
                                </Button>
                                {isAdmin() && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-300"
                                    onClick={() => {
                                      const photoId = selectedSolicitud.confirmacion_entrega.photo_url?.split('/').pop();
                                      if (photoId) deleteDeliveryImage(photoId, 'photo');
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Ubicación GPS */}
                        {selectedSolicitud.confirmacion_entrega.latitude && selectedSolicitud.confirmacion_entrega.longitude && (
                          <div className="pt-3 border-t border-green-200 dark:border-green-800">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const lat = selectedSolicitud.confirmacion_entrega.latitude;
                                const lng = selectedSolicitud.confirmacion_entrega.longitude;
                                window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                              }}
                              className="w-full border-green-300 text-green-600 hover:bg-green-100"
                            >
                              <MapPin className="w-4 h-4 mr-2" />
                              Ver ubicación de entrega en Google Maps
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Devuelta - Actions for ANY user to edit and resubmit */}
                  {selectedSolicitud.estado === "devuelto" && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <RotateCcw className="w-5 h-5 text-orange-600" />
                        <Label className="text-sm font-semibold text-orange-700 dark:text-orange-300">Solicitud Devuelta</Label>
                      </div>
                      {selectedSolicitud.motivo_devolucion && (
                        <p className="text-sm text-orange-700 dark:text-orange-300 mb-1"><strong>Motivo:</strong> {selectedSolicitud.motivo_devolucion}</p>
                      )}
                      {selectedSolicitud.devuelto_por && (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mb-3">Devuelto por: {selectedSolicitud.devuelto_por}</p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => { setShowViewDialog(false); openEditDialog(selectedSolicitud); }}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="edit-devuelta-btn"
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Editar y Corregir
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              await updateEstado(selectedSolicitud.id, "pendiente");
                              setSelectedSolicitud({...selectedSolicitud, estado: "pendiente"});
                              toast.success("Solicitud reenviada como pendiente");
                            } catch (e) { toast.error("Error al reenviar"); }
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="reenviar-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Volver a Enviar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Historial de cambios */}
                  {selectedSolicitud.historial_cambios && selectedSolicitud.historial_cambios.length > 0 && (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                      <Label className="mb-2 block text-sm font-semibold">Historial de Cambios</Label>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {[...selectedSolicitud.historial_cambios].reverse().map((h, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs bg-slate-50 dark:bg-slate-700/50 rounded p-2">
                            <Clock className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                            <div>
                              <span className="text-slate-500 dark:text-slate-400">{new Date(h.fecha).toLocaleString("es-DO")}</span>
                              <span className="mx-1">-</span>
                              <span className="font-medium text-slate-700 dark:text-slate-200">{h.usuario_nombre}</span>
                              <span className="mx-1">:</span>
                              <Badge className="bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 text-xs border-0 mx-0.5">{h.estado_anterior || "?"}</Badge>
                              <span className="mx-1">→</span>
                              <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs border-0">{h.estado_nuevo}</Badge>
                              {h.motivo && <p className="text-slate-500 dark:text-slate-400 mt-0.5 italic">Motivo: {h.motivo}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Estado actions - Solo visible para roles autorizados */}
                  {canChangeStatus() && (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                      <Label className="mb-2 block">Cambiar Estado</Label>
                      <div className="flex flex-wrap gap-2">
                        {["pendiente", "en_proceso", "completado", "devuelto", "cancelado"].map((estado) => (
                          <Button
                            key={estado}
                            variant={selectedSolicitud.estado === estado ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (estado === "completado" && selectedSolicitud.estado !== "completado") {
                                setShowViewDialog(false);
                                handleCompletadoClick(selectedSolicitud);
                              } else {
                                updateEstado(selectedSolicitud.id, estado);
                                setSelectedSolicitud({...selectedSolicitud, estado});
                              }
                            }}
                            className={selectedSolicitud.estado === estado 
                              ? (estado === "completado" ? "bg-green-600 text-white" : estado === "devuelto" ? "bg-orange-500 text-white" : "bg-[#0C314C] text-white") 
                              : (estado === "completado" ? "border-green-500 text-green-600 hover:bg-green-50" : estado === "devuelto" ? "border-orange-500 text-orange-600 hover:bg-orange-50" : "")}
                          >
                            {estado === "completado" && <PackageCheck className="w-4 h-4 mr-1" />}
                            {estado === "devuelto" && <RotateCcw className="w-4 h-4 mr-1" />}
                            {estado.replace("_", " ")}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                    Cerrar
                  </Button>
                  {(canEdit(selectedSolicitud) || selectedSolicitud?.estado === "devuelto") && (
                    <Button 
                      onClick={() => {
                        setShowViewDialog(false);
                        openEditDialog(selectedSolicitud);
                      }}
                      className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#0C314C]" />
                Importar Solicitudes desde Excel
              </DialogTitle>
              <DialogDescription>
                {bulkStep === 1 && "Sube un archivo Excel (.xlsx) con los datos de las solicitudes"}
                {bulkStep === 2 && "Verifica y ajusta el mapeo de columnas generado por la IA"}
                {bulkStep === 3 && "Resultado de la importación"}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 py-4 border-b dark:border-slate-700">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 1 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
                Subir
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 2 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                Mapear
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 3 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">3</span>
                Resultado
              </div>
            </div>

            <div className="flex-1 overflow-auto py-4">
              {/* Step 1: Upload */}
              {bulkStep === 1 && (
                <div className="flex flex-col items-center justify-center py-12">
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleBulkFileUpload}
                    className="hidden"
                  />
                  
                  <div className="w-20 h-20 rounded-full bg-[#0C314C]/10 flex items-center justify-center mb-6">
                    <FileSpreadsheet className="w-10 h-10 text-[#0C314C]" />
                  </div>
                  
                  {bulkLoading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#0C314C] mx-auto mb-4" />
                      <p className="text-slate-600 dark:text-slate-400">Analizando archivo con IA...</p>
                    </div>
                  ) : (
                    <>
                      <Button 
                        onClick={() => bulkFileInputRef.current?.click()}
                        className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white mb-4"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Seleccionar Archivo Excel
                      </Button>
                      <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
                        La IA analizará las columnas de tu archivo y las mapeará automáticamente a los campos de solicitud correspondientes.
                      </p>
                      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                        <p className="font-medium mb-2">Campos requeridos:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Cliente (nombre del solicitante)</li>
                          <li>Dirección de envío</li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Mapping */}
              {bulkStep === 2 && bulkPreview && (
                <div className="space-y-6">
                  {/* AI Recommendation Note */}
                  <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-300">Mapeo automático completado</p>
                      <p className="text-sm text-green-700 dark:text-green-400">
                        La IA ha analizado tu archivo y recomendado los mapeos. Puedes modificarlos si es necesario.
                      </p>
                    </div>
                  </div>

                  {/* Mapping Section */}
                  <div>
                    <h3 className="font-semibold mb-3 text-slate-900 dark:text-white flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Mapeo de Columnas
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {bulkPreview.headers.map(header => (
                        <div key={header} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate" title={header}>
                            {header}
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <Select 
                            value={bulkMapping[header] || "_none"} 
                            onValueChange={(value) => updateBulkMapping(header, value === "_none" ? "" : value)}
                          >
                            <SelectTrigger className="w-44 text-xs h-8">
                              <SelectValue placeholder="No mapear" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">No mapear</SelectItem>
                              {solicitudFields.map(field => (
                                <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Defaults */}
                  <div className="flex gap-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs text-amber-700 dark:text-amber-300">Tipo por defecto</Label>
                      <Select 
                        value={bulkPreview.default_tipo} 
                        onValueChange={(value) => setBulkPreview({...bulkPreview, default_tipo: value})}
                      >
                        <SelectTrigger className="h-8 text-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="entrega">Entrega</SelectItem>
                          <SelectItem value="recogida">Recogida</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-amber-700 dark:text-amber-300">¿Urgente por defecto?</Label>
                      <Select 
                        value={bulkPreview.default_urgente ? "true" : "false"} 
                        onValueChange={(value) => setBulkPreview({...bulkPreview, default_urgente: value === "true"})}
                      >
                        <SelectTrigger className="h-8 text-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="false">No</SelectItem>
                          <SelectItem value="true">Sí</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Preview Table */}
                  <div>
                    <h3 className="font-semibold mb-3 text-slate-900 dark:text-white">
                      Vista previa ({bulkPreview.total_rows} registros)
                    </h3>
                    <ScrollArea className="h-48 border rounded-lg dark:border-slate-700">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                          <tr>
                            {bulkPreview.headers.slice(0, 5).map(h => (
                              <th key={h} className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                          {bulkPreview.sample_data.map((row, idx) => (
                            <tr key={idx}>
                              {bulkPreview.headers.slice(0, 5).map(h => (
                                <td key={h} className="p-2 text-slate-700 dark:text-slate-300">{row[h] || '-'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                </div>
              )}

              {/* Step 3: Results */}
              {bulkStep === 3 && bulkResult && (
                <div className="flex flex-col items-center py-8">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                    bulkResult.created > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                  }`}>
                    {bulkResult.created > 0 ? (
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    ) : (
                      <AlertCircle className="w-10 h-10 text-amber-600" />
                    )}
                  </div>

                  <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-white">
                    {bulkResult.created > 0 ? "Importación Completada" : "Importación con Advertencias"}
                  </h3>
                  
                  <div className="flex gap-8 mb-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{bulkResult.created}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Creadas</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-amber-600">{bulkResult.skipped}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Omitidas</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-slate-600 dark:text-slate-300">{bulkResult.total_processed}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Total</p>
                    </div>
                  </div>

                  {bulkResult.errors.length > 0 && (
                    <div className="w-full max-w-lg">
                      <h4 className="font-medium mb-2 text-slate-700 dark:text-slate-300">Detalles de errores:</h4>
                      <ScrollArea className="h-32 border rounded-lg p-3 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
                        {bulkResult.errors.map((err, idx) => (
                          <p key={idx} className="text-sm text-red-700 dark:text-red-300 mb-1">{err}</p>
                        ))}
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="border-t dark:border-slate-700 pt-4">
              {bulkStep === 1 && (
                <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
                  Cancelar
                </Button>
              )}
              {bulkStep === 2 && (
                <>
                  <Button variant="outline" onClick={() => setBulkStep(1)}>
                    Volver
                  </Button>
                  <Button 
                    onClick={executeBulkImport}
                    disabled={bulkLoading || !Object.values(bulkMapping).includes('cliente') || !Object.values(bulkMapping).includes('direccion_envio')}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  >
                    {bulkLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    {bulkLoading ? "Importando..." : `Importar ${bulkPreview?.total_rows || 0} Solicitudes`}
                  </Button>
                </>
              )}
              {bulkStep === 3 && (
                <Button onClick={() => setShowBulkDialog(false)} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                  Cerrar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Cliente Dialog */}
        <Dialog open={showNewClienteDialog} onOpenChange={setShowNewClienteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                Nuevo Cliente
              </DialogTitle>
              <DialogDescription>
                Agregue un nuevo cliente a la base de datos
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre del Cliente *</Label>
                <Input
                  value={newClienteData.nombre}
                  onChange={(e) => setNewClienteData({...newClienteData, nombre: e.target.value})}
                  placeholder="Nombre completo o empresa"
                  data-testid="new-cliente-nombre"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  value={newClienteData.direccion}
                  onChange={(e) => setNewClienteData({...newClienteData, direccion: e.target.value})}
                  placeholder="Dirección completa"
                  data-testid="new-cliente-direccion"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={newClienteData.telefono}
                    onChange={(e) => setNewClienteData({...newClienteData, telefono: e.target.value})}
                    placeholder="809-000-0000"
                    data-testid="new-cliente-telefono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Celular</Label>
                  <Input
                    value={newClienteData.celular}
                    onChange={(e) => setNewClienteData({...newClienteData, celular: e.target.value})}
                    placeholder="809-000-0000"
                    data-testid="new-cliente-celular"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Sucursal
                </Label>
                <Select
                  value={newClienteData.sucursal}
                  onValueChange={(value) => setNewClienteData({...newClienteData, sucursal: value})}
                >
                  <SelectTrigger data-testid="new-cliente-sucursal">
                    <SelectValue placeholder="Seleccionar sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="N/A">N/A (Sin sucursal)</SelectItem>
                    {SUCURSALES_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewClienteDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateNewCliente} className="bg-blue-600 hover:bg-blue-700">
                Crear y Seleccionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reporte Mensual Dialog */}
        <Dialog open={showReporteMensualDialog} onOpenChange={setShowReporteMensualDialog}>
          <DialogContent className="max-w-2xl" data-testid="reporte-mensual-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileBarChart className="w-5 h-5 text-blue-600" />
                Reporte Mensual de Mensajería
              </DialogTitle>
              <DialogDescription>
                Configura el envío automático mensual con PDF ejecutivo + Excel detallado,
                o envía/previsualiza el reporte al momento.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Activar / Desactivar */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-slate-50 dark:bg-slate-800">
                <div>
                  <Label className="font-medium">Envío mensual automático</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Cuando está activo, el sistema envía el reporte el día y hora configurados.
                  </p>
                </div>
                <Switch
                  checked={reporteMensualForm.activo}
                  onCheckedChange={(v) => setReporteMensualForm({ ...reporteMensualForm, activo: v })}
                  data-testid="reporte-mensual-activo-switch"
                />
              </div>

              {/* Día + Hora */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Día del mes (1-28)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={28}
                    value={reporteMensualForm.dia_envio}
                    onChange={(e) => setReporteMensualForm({ ...reporteMensualForm, dia_envio: parseInt(e.target.value) || 1 })}
                    data-testid="reporte-mensual-dia-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Hora (HH:MM, 24h)</Label>
                  <Input
                    type="time"
                    value={reporteMensualForm.hora_envio}
                    onChange={(e) => setReporteMensualForm({ ...reporteMensualForm, hora_envio: e.target.value })}
                    data-testid="reporte-mensual-hora-input"
                  />
                </div>
              </div>

              {/* Destinatarios */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Destinatarios</Label>
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <Checkbox
                      checked={reporteMensualForm.incluir_admin_original}
                      onCheckedChange={(v) => setReporteMensualForm({ ...reporteMensualForm, incluir_admin_original: !!v })}
                    />
                    Incluir mi correo ({reporteMensualConfig?.admin_email || user?.email || "—"})
                  </label>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="agregar@ejemplo.com"
                    value={reporteMensualEmailInput}
                    onChange={(e) => setReporteMensualEmailInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddReporteEmail(); } }}
                    data-testid="reporte-mensual-email-input"
                  />
                  <Button type="button" variant="outline" onClick={handleAddReporteEmail} data-testid="reporte-mensual-add-email-btn">
                    Agregar
                  </Button>
                </div>
                {(reporteMensualForm.emails_adicionales || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {reporteMensualForm.emails_adicionales.map((em) => (
                      <Badge key={em} variant="secondary" className="pl-2 pr-1 py-1 gap-1">
                        <Mail className="w-3 h-3" /> {em}
                        <button
                          type="button"
                          onClick={() => handleRemoveReporteEmail(em)}
                          className="ml-1 hover:text-red-600"
                          data-testid={`reporte-remove-${em}`}
                        >×</button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Último envío */}
              {reporteMensualConfig?.ultimo_envio && (
                <div className="text-xs text-slate-500 border-t pt-2">
                  Último envío: {new Date(reporteMensualConfig.ultimo_envio).toLocaleString("es-DO")}
                  {reporteMensualConfig.ultimo_mes_enviado && ` · Mes reportado: ${reporteMensualConfig.ultimo_mes_enviado}`}
                </div>
              )}

              {/* Historial de envíos con opción a reenviar */}
              <div className="border rounded-lg overflow-hidden" data-testid="reporte-mensual-historial">
                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    Historial de envíos ({reporteMensualHistorial.length})
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={fetchReporteMensualHistorial}
                    className="h-6 text-xs"
                    data-testid="reporte-historial-refresh-btn"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Refrescar
                  </Button>
                </div>
                {reporteMensualHistorial.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-slate-500">
                    Aún no se ha enviado ningún reporte. Al presionar <b>Enviar ahora</b> o cuando el envío automático dispare, aparecerá aquí con opción de <b>Reenviar</b>.
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-900 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Mes</th>
                          <th className="px-2 py-1.5 text-left">Fecha envío</th>
                          <th className="px-2 py-1.5 text-center">Dest.</th>
                          <th className="px-2 py-1.5 text-center">✓ / ✗</th>
                          <th className="px-2 py-1.5 text-left">Origen</th>
                          <th className="px-2 py-1.5 text-right">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reporteMensualHistorial.map((h) => (
                          <tr key={h.id} className="hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`reporte-historial-row-${h.id}`}>
                            <td className="px-2 py-1.5 font-medium">{h.mes_nombre || h.mes_etiqueta}</td>
                            <td className="px-2 py-1.5">{(() => {
                              try {
                                const d = h.fecha_envio ? new Date(h.fecha_envio) : null;
                                if (!d || isNaN(d.getTime())) return "—";
                                return d.toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" });
                              } catch { return "—"; }
                            })()}</td>
                            <td className="px-2 py-1.5 text-center">{h.total_destinatarios}</td>
                            <td className="px-2 py-1.5 text-center">
                              <span className="text-green-700">{h.exitosos}</span>
                              {h.fallidos > 0 && <span className="text-red-600"> / {h.fallidos}</span>}
                            </td>
                            <td className="px-2 py-1.5 text-slate-500">
                              {h.triggered_by?.startsWith("scheduler") ? "Auto" :
                               h.triggered_by?.startsWith("resend") ? "Reenvío" : "Manual"}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResendReporteMensual(h)}
                                disabled={reporteMensualResendingId === h.id}
                                className="h-6 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                                data-testid={`reporte-historial-resend-${h.id}`}
                              >
                                {reporteMensualResendingId === h.id ? "Enviando..." : "Reenviar"}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handlePreviewReporteMensual("pdf")}
                  data-testid="reporte-mensual-preview-pdf-btn"
                >
                  Previsualizar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePreviewReporteMensual("xlsx")}
                  data-testid="reporte-mensual-preview-xlsx-btn"
                >
                  Descargar Excel
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleSaveReporteMensualConfig}
                  disabled={reporteMensualSavingFlag}
                  data-testid="reporte-mensual-save-btn"
                >
                  {reporteMensualSavingFlag ? "Guardando..." : "Guardar configuración"}
                </Button>
                <Button
                  onClick={handleSendReporteMensualNow}
                  disabled={reporteMensualSendingFlag}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="reporte-mensual-send-now-btn"
                >
                  {reporteMensualSendingFlag ? "Enviando..." : "Enviar ahora"}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Diagnóstico de imágenes de acuses (admin) */}
        <Dialog open={showDiagnostico} onOpenChange={setShowDiagnostico}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="diagnostico-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Diagnóstico de imágenes de acuses
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-1">
                Verifica el estado de las firmas y fotos de entrega almacenadas. Si algún acuse muestra "Internal Server Error" al verlo, acá te decimos por qué.
              </p>
            </DialogHeader>

            {diagnosticoLoading && (
              <div className="py-8 text-center" data-testid="diagnostico-loading">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
                <p className="text-sm text-slate-500 mt-2">Analizando registros…</p>
              </div>
            )}

            {!diagnosticoLoading && diagnosticoError && (
              <div className="my-4 p-4 rounded-lg border-2 border-rose-300 bg-rose-50" data-testid="diagnostico-error">
                <p className="text-sm font-bold text-rose-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  No se pudo cargar el diagnóstico
                </p>
                <p className="text-xs text-rose-800 mt-2 font-mono break-words">{diagnosticoError}</p>
                <p className="text-[11px] text-rose-700 mt-2">
                  Posibles causas: (1) la última versión del backend aún no está desplegada en producción —
                  hacé Redeploy desde la plataforma; (2) tu sesión expiró — cerrá sesión y volvé a entrar;
                  (3) no tenés rol de administrador.
                </p>
                <Button
                  onClick={() => setDiagnosticoNonce((n) => n + 1)}
                  className="mt-3 bg-rose-600 hover:bg-rose-700 text-white text-xs h-8"
                  data-testid="diagnostico-retry-btn"
                >
                  Reintentar
                </Button>
              </div>
            )}

            {!diagnosticoLoading && diagnosticoData && diagnosticoData.computing && (
              <div className="my-4 p-4 rounded-lg border-2 border-amber-300 bg-amber-50" data-testid="diagnostico-computing">
                <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Calculando diagnóstico por primera vez…
                </p>
                <p className="text-xs text-amber-800 mt-2">
                  {diagnosticoData.causa_probable || "El cálculo se está ejecutando en segundo plano para evitar timeouts en colecciones grandes. En unos segundos podrás ver los resultados."}
                </p>
                <Button
                  onClick={() => setDiagnosticoNonce((n) => n + 1)}
                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                  data-testid="diagnostico-reload-btn"
                >
                  Recargar
                </Button>
              </div>
            )}

            {!diagnosticoLoading && diagnosticoData && !diagnosticoData.computing && (
              <div className="space-y-4">
                {/* KPIs */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-700">{diagnosticoData.total_registros}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Total registros</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{diagnosticoData.con_binario_intacto}</p>
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700">Visualizables ✓</p>
                  </div>
                  <div className={`border rounded-lg p-3 text-center ${diagnosticoData.binario_perdido ? "bg-rose-50 border-rose-300" : "bg-emerald-50 border-emerald-200"}`}>
                    <p className={`text-2xl font-bold ${diagnosticoData.binario_perdido ? "text-rose-700" : "text-emerald-700"}`}>
                      {diagnosticoData.binario_perdido}
                    </p>
                    <p className={`text-[10px] uppercase tracking-wider ${diagnosticoData.binario_perdido ? "text-rose-700" : "text-emerald-700"}`}>
                      Sin binario ({diagnosticoData.porcentaje_perdido}%)
                    </p>
                  </div>
                </div>

                {/* Causa probable */}
                <div className={`p-3 rounded border text-sm ${diagnosticoData.binario_perdido ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
                  <p className="font-bold mb-1">Diagnóstico:</p>
                  <p>{diagnosticoData.causa_probable}</p>
                  {diagnosticoData.recuperacion && (
                    <p className="mt-2"><b>Recuperación:</b> {diagnosticoData.recuperacion}</p>
                  )}
                </div>

                {/* Lista de afectados recientes */}
                {Array.isArray(diagnosticoData.afectados_recientes) && diagnosticoData.afectados_recientes.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                      Últimos {diagnosticoData.afectados_recientes.length} acuses afectados
                    </p>
                    <div className="max-h-60 overflow-y-auto border border-slate-200 rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left">Fecha</th>
                            <th className="px-2 py-1.5 text-left">Tipo</th>
                            <th className="px-2 py-1.5 text-left">Subió</th>
                            <th className="px-2 py-1.5 text-left">Solicitud</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {diagnosticoData.afectados_recientes.map((a) => (
                            <tr key={a.id} className="hover:bg-rose-50/30">
                              <td className="px-2 py-1.5">{(a.uploaded_at || "—").substring(0, 10)}</td>
                              <td className="px-2 py-1.5">
                                <Badge variant="outline" className="text-[9px]">
                                  {a.type === "delivery_signature" ? "Firma" : a.type === "delivery_photo" ? "Foto" : a.type}
                                </Badge>
                              </td>
                              <td className="px-2 py-1.5 text-slate-600">{a.uploaded_by || "—"}</td>
                              <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{(a.solicitud_id || "—").substring(0, 8)}…</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Distribución por mes */}
                {diagnosticoData.distribucion_por_mes && Object.keys(diagnosticoData.distribucion_por_mes).length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Distribución por mes (registros afectados)</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(diagnosticoData.distribucion_por_mes).map(([mes, cnt]) => (
                        <Badge key={mes} variant="outline" className="text-[10px]">
                          {mes}: <span className="font-bold ml-1">{cnt}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Acciones recomendadas */}
                {diagnosticoData.binario_perdido > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                    <p className="font-bold mb-1">¿Qué hacer?</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>El bug que causaba esto YA fue corregido (los backups ahora preservan el binario y los restores son no destructivos).</li>
                      <li>Para los acuses afectados que aún necesitás ver, pedile al mensajero que reenvíe la foto/firma desde su dispositivo.</li>
                      <li>Una vez subida, el sistema la asociará automáticamente al acuse existente.</li>
                    </ol>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDiagnostico(false)} data-testid="diagnostico-close-btn">
                Cerrar
              </Button>
              {diagnosticoData && !diagnosticoData.computing && (
                <Button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("token");
                      const apiUrl = process.env.REACT_APP_BACKEND_URL;
                      await axios.post(`${apiUrl}/api/entregas/diagnostico/recalcular`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      toast.success("Recálculo en proceso. En unos segundos podrás ver el resultado actualizado.");
                      setTimeout(() => setDiagnosticoNonce((n) => n + 1), 5000);
                    } catch (e) {
                      toast.error("No se pudo lanzar el recálculo");
                    }
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="diagnostico-refresh-btn"
                >
                  <Loader2 className="w-4 h-4 mr-2" />
                  Recalcular
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Verificar Imagen Dialog — admin only */}
        <Dialog open={showVerificarImagen} onOpenChange={setShowVerificarImagen}>
          <DialogContent className="max-w-2xl" data-testid="verificar-imagen-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-sky-600" />
                Verificar Imagen de Acuse
              </DialogTitle>
              <DialogDescription>
                Pegá la URL completa del endpoint de imagen (o solo el asset_id) que veas con problema. Te diré exactamente qué está pasando y qué hacer.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="verificar-url" className="text-sm font-medium">URL o asset_id</Label>
                <Input
                  id="verificar-url"
                  type="text"
                  value={verificarUrl}
                  onChange={(e) => setVerificarUrl(e.target.value)}
                  placeholder="https://...emergent.host/api/entregas/imagen/8e8aaf42-3d9d-... o solo el UUID"
                  className="mt-1 font-mono text-xs"
                  data-testid="verificar-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !verificandoImagen) handleVerificarImagen(); }}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Tip: hacé clic derecho sobre la imagen rota → "Copiar dirección del enlace" y pegala acá.
                </p>
              </div>

              <Button
                onClick={handleVerificarImagen}
                disabled={verificandoImagen || !verificarUrl.trim()}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="verificar-submit-btn"
              >
                {verificandoImagen ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...</>
                ) : (
                  <><Stethoscope className="w-4 h-4 mr-2" /> Verificar ahora</>
                )}
              </Button>

              {/* Botón secundario: chequear estado de índices BD */}
              <Button
                onClick={async () => {
                  setVerificandoImagen(true);
                  try {
                    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
                    const apiUrl = process.env.REACT_APP_BACKEND_URL;
                    const r = await axios.get(`${apiUrl}/api/entregas/diagnostico/indices`, {
                      headers: { Authorization: `Bearer ${token}` },
                      timeout: 30000,
                    });
                    const d = r.data;
                    const nivel = d.estado === "OK" ? "ok" : "error";
                    setVerificarResultado({
                      ok: nivel === "ok",
                      nivel,
                      status: d.estado,
                      ms: 0,
                      veredicto: d.estado === "OK" ? `Índices BD OK ✅ (${d.total_docs} docs)` : `Falta índice ❌`,
                      mensaje: `Índices presentes: ${(d.indices || []).join(", ")}. Total docs: ${d.total_docs}. Dupes en \`id\`: ${(d.dupes_id_muestra || []).length === 0 ? "ninguno" : "SÍ encontrados → " + JSON.stringify(d.dupes_id_muestra)}.`,
                      recomendacion: d.recomendacion,
                      diag: d,
                    });
                  } catch (e) {
                    toast.error(`Error: ${e.response?.data?.detail || e.message}`);
                  } finally {
                    setVerificandoImagen(false);
                  }
                }}
                disabled={verificandoImagen}
                variant="outline"
                className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="verificar-indices-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Diagnosticar índices BD (si seguís viendo 503)
              </Button>

              {/* Botón terciario: crear índices manualmente */}
              <Button
                onClick={async () => {
                  if (!window.confirm("¿Crear/asegurar los índices de delivery_assets en BD? Esto es idempotente y seguro.")) return;
                  setVerificandoImagen(true);
                  try {
                    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
                    const apiUrl = process.env.REACT_APP_BACKEND_URL;
                    const r = await axios.post(`${apiUrl}/api/entregas/diagnostico/indices/crear`, {}, {
                      headers: { Authorization: `Bearer ${token}` },
                      timeout: 60000,
                    });
                    const d = r.data;
                    if ((d.errores || []).length > 0) {
                      toast.error(`Errores: ${JSON.stringify(d.errores)}`, { duration: 12000 });
                    } else {
                      toast.success(`✅ Índices creados/asegurados: ${(d.creados || []).map(c => c.nombre).join(", ")}. Probá ahora la URL de imagen — debería responder en <1s.`, { duration: 12000 });
                    }
                  } catch (e) {
                    toast.error(`Error: ${e.response?.data?.detail || e.message}`);
                  } finally {
                    setVerificandoImagen(false);
                  }
                }}
                disabled={verificandoImagen}
                variant="outline"
                className="w-full border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                data-testid="crear-indices-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Crear índices BD ahora (fix definitivo del 503)
              </Button>

              {/* Botón cuaternario: SOLUCIÓN DEFINITIVA — migrar binarios a disco */}
              <Button
                onClick={async () => {
                  if (!window.confirm(
                    "🚀 SOLUCIÓN DEFINITIVA del 503\n\n" +
                    "Va a migrar los binarios (~10GB) de la BD al filesystem.\n" +
                    "Después de esto la BD pesa ~2MB y los índices se crean al instante.\n\n" +
                    "Tarda varios minutos. Corre en background — podés cerrar este diálogo.\n\n" +
                    "¿Iniciar migración?"
                  )) return;
                  setVerificandoImagen(true);
                  const token = sessionStorage.getItem("token") || localStorage.getItem("token");
                  const apiUrl = process.env.REACT_APP_BACKEND_URL;
                  try {
                    await axios.post(`${apiUrl}/api/entregas/migrar-a-disco`, {}, {
                      headers: { Authorization: `Bearer ${token}` },
                      timeout: 30000,
                    });
                    toast.success("🚀 Migración iniciada en segundo plano. Te aviso cuando termine.", { duration: 6000 });

                    // Polling de progreso
                    const start = Date.now();
                    const MAX_MS = 60 * 60 * 1000; // 1 hora máx
                    const tick = async () => {
                      if (Date.now() - start > MAX_MS) {
                        toast.warning("⏰ La migración lleva más de 1h. Consultá los logs.", { duration: 8000 });
                        setVerificandoImagen(false);
                        return;
                      }
                      try {
                        const s = await axios.get(`${apiUrl}/api/entregas/migrar-a-disco/status`, {
                          headers: { Authorization: `Bearer ${token}` },
                          timeout: 20000,
                        });
                        const st = s.data || {};
                        setVerificarResultado({
                          ok: !st.running,
                          nivel: st.running ? "warn" : "ok",
                          status: st.running ? "MIGRANDO" : "TERMINADO",
                          ms: 0,
                          veredicto: st.running
                            ? `🚚 Migrando... ${st.migrated_to_disk + st.already_on_disk} procesados (errors: ${st.errors})`
                            : `✅ Migración completa: ${st.migrated_to_disk} migrados, ${st.already_on_disk} ya estaban en disco, ${st.errors} errores`,
                          mensaje: st.running
                            ? `En curso desde ${st.started_at}. Doc actual: ${st.current_id || "—"}.`
                            : `Finalizada ${st.finished_at}. La BD ya no tiene binarios in-line. Probá ahora la URL — debería responder al instante.`,
                          recomendacion: st.running
                            ? "Podés cerrar este diálogo, la migración sigue en background. Volvé y abrí 'Diagnosticar índices BD' después."
                            : "Hacé clic en 'Diagnosticar índices BD' para verificar que ahora estén creados. Después probá una URL de imagen.",
                          diag: st,
                        });
                        if (st.running) {
                          setTimeout(tick, 5000); // cada 5s
                        } else {
                          setVerificandoImagen(false);
                          if (st.errors === 0) {
                            toast.success(`✅ Migración completa: ${st.migrated_to_disk} migrados.`, { duration: 12000 });
                          } else {
                            toast.warning(`⚠️ Migración terminada con ${st.errors} errores. Ver detalle.`, { duration: 12000 });
                          }
                        }
                      } catch (pollErr) {
                        setTimeout(tick, 8000);
                      }
                    };
                    setTimeout(tick, 3000);
                  } catch (e) {
                    toast.error(`Error al iniciar migración: ${e.response?.data?.detail || e.message}`);
                    setVerificandoImagen(false);
                  }
                }}
                disabled={verificandoImagen}
                variant="outline"
                className="w-full border-purple-400 text-purple-700 hover:bg-purple-50 font-semibold"
                data-testid="migrar-disco-btn"
              >
                <PackageCheck className="w-4 h-4 mr-2" />
                🚀 Migrar binarios a disco (SOLUCIÓN DEFINITIVA)
              </Button>

              {verificarResultado && (
                <div
                  className={`rounded-lg border-2 p-4 space-y-2 ${
                    verificarResultado.nivel === "ok" ? "border-emerald-300 bg-emerald-50" :
                    verificarResultado.nivel === "warn" ? "border-amber-300 bg-amber-50" :
                    "border-rose-300 bg-rose-50"
                  }`}
                  data-testid="verificar-resultado"
                >
                  <div className="font-bold text-base">{verificarResultado.veredicto}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono bg-white/60 rounded p-2">
                    <div><span className="text-slate-500">HTTP:</span> {verificarResultado.status}</div>
                    <div><span className="text-slate-500">Tiempo:</span> {verificarResultado.ms} ms</div>
                    {verificarResultado.recoveredFrom && (
                      <div className="col-span-2"><span className="text-slate-500">Origen:</span> {verificarResultado.recoveredFrom}</div>
                    )}
                    {verificarResultado.assetId && (
                      <div className="col-span-2 break-all"><span className="text-slate-500">Asset ID:</span> {verificarResultado.assetId}</div>
                    )}
                  </div>
                  <div className="text-sm text-slate-700">
                    <span className="font-semibold">¿Qué pasó?</span> {verificarResultado.mensaje}
                  </div>
                  <div className="text-sm text-slate-800 bg-white/80 rounded p-2 border border-slate-200">
                    <span className="font-semibold">📋 Recomendación:</span> {verificarResultado.recomendacion}
                  </div>
                  {verificarResultado.diag && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Ver diagnóstico técnico completo</summary>
                      <pre className="mt-2 bg-slate-900 text-emerald-300 p-2 rounded overflow-auto text-[10px]">
                        {JSON.stringify(verificarResultado.diag, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVerificarImagen(false)} data-testid="verificar-close-btn">
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
